import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import {
  assetFileDisplayName,
  getAssetDocuments,
  getAssetVideoPlayback,
  getSelectableSubscriptionLessons,
  getSubscriptionLessons,
} from '../utils/assetDisplayName';
import { restrictedVideoProps } from '../utils/restrictedVideo';
import { GRADE_LEVELS } from '../utils/gradeLevels';
import BaseDashboard from '../components/BaseDashboard';
import MissionForm from '../components/MissionForm';
import MissionModal from '../components/MissionModalDropdown';
import DragDropFileUpload from '../components/DragDropFileUpload';
import Modal from '../components/Modal';
import TeacherMessagesTab from '../components/TeacherDashboardTabs/TeacherMessagesTab';
import TeacherMissionMonitoring from '../components/TeacherMissionMonitoring';
import StudentProgressMonitoring from '../components/StudentProgressMonitoring';
import TeacherMissionViewer from '../components/TeacherMissionViewer';
import Toast from '../components/Toast';
import GradingModal from '../components/GradingModal';
import NotificationModal from '../components/NotificationModal';
import QuizCreationModal from '../components/QuizCreationModal';
import QuizManagement from '../components/QuizManagement';
import ContentLibrary from '../components/ContentLibrary';
import ProgressDashboard from '../components/ProgressDashboard';
import ImpersonationBanner from '../components/ImpersonationBanner';
import './TeacherDashboard.css';
import styles from './TeacherDashboard.module.css';
import bulkUploadStyles from './BulkUploadModal.module.css';

// Simple Markdown renderer for basic formatting
const SimpleMarkdown = ({ children }) => {
  if (!children) return null;
  
  const text = children.toString();
  
  // Convert basic Markdown to HTML
  let html = text
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Headers: # Header -> <h4>Header</h4>
    .replace(/^### (.*$)/gim, '<h5>$1</h5>')
    .replace(/^## (.*$)/gim, '<h4>$1</h4>')
    .replace(/^# (.*$)/gim, '<h3>$1</h3>')
    // Lists: - item -> <li>item</li>
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    // Line breaks
    .replace(/\n/g, '<br />');
  
  // Wrap lists in <ul> tags
  if (html.includes('<li>')) {
    html = html.replace(/(<li>.*?<\/li>)/g, '<ul>$1</ul>');
  }
  
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

/** Split scheme-of-work HTML into intro (before first unit) and array of unit blocks for per-unit buttons. */
function splitSchemeIntoUnits(html) {
  if (!html || typeof html !== 'string') return { intro: html, units: [] };
  // Match spacer+Unit N: or <br>+Unit N: so we catch every unit
  const unitBlockRegex = /((?:<span class="scheme-unit-spacer"><\/span>|<br\s*\/?>)\s*Unit\s+\d+\s*:)/gi;
  const parts = html.split(unitBlockRegex);
  if (parts.length < 2) return { intro: html, units: [] };
  const intro = parts[0].trim();
  const units = [];
  for (let i = 1; i < parts.length; i += 2) {
    const header = parts[i] || '';
    const body = parts[i + 1] || '';
    if (header) units.push({ content: header + body });
  }
  return { intro, units };
}

/** Insert vertical spacers between units and between Curriculum links / Lesson breakdown in scheme-of-work HTML. */
function addSchemeSpacers(html) {
  if (!html || typeof html !== 'string') return html;
  return html
    .replace(/(<br\s*\/?>)\s*Unit\s+(\d)/gi, '$1<span class="scheme-unit-spacer"></span>Unit $2')
    .replace(/(<br\s*\/?>)\s*Curriculum\s+links/gi, '$1<span class="scheme-block-spacer"></span>Curriculum links')
    .replace(/(<br\s*\/?>)\s*Lesson\s+breakdown/gi, '$1<span class="scheme-block-spacer"></span>Lesson breakdown');
}

/** Turn each Lesson breakdown line (e.g. "1. Title") into a clickable link for creating a lesson plan. */
function addLessonPlanLinks(html) {
  if (!html || typeof html !== 'string') return html;
  return html.replace(
    /(<br\s*\/?>)\s*(\d+\.\s+[^<]+?)(?=<br\s*\/?>|$)/gi,
    (_, br, title) => {
      const t = title.trim();
      const safe = t.replace(/"/g, '&quot;');
      return `${br}<a href="#" class="lesson-plan-link" data-lesson-title="${safe}">${t}</a>`;
    }
  );
}

/** Format curriculum match as scheme of work: three sections separated by two horizontal lines. */
function formatAsSchemeOfWork(text) {
  if (!text || typeof text !== 'string') return text;
  const hr = '<hr class="scheme-of-work-divider" />';
  const toBr = (s) => String(s).replace(/\n/g, '<br>');
  const parts = text.split(/^\s*---\s*$/gm).map(s => s.trim()).filter(Boolean);
  let html;
  if (parts.length >= 3) {
    const third = parts.length > 3 ? parts.slice(2).join('\n\n').trim() : (parts[2] || '');
    html = toBr(parts[0]) + hr + toBr(parts[1]) + hr + toBr(third);
  } else if (parts.length === 2) {
    html = toBr(parts[0]) + hr + toBr(parts[1]) + hr + toBr('Remaining units and lessons.');
  } else {
    const one = parts[0] || text;
    let sec2 = one.search(/\n\s*Spring Term\s*\n/i);
    if (sec2 < 0) sec2 = one.search(/\n\s*Autumn Term\s*\n/i);
    if (sec2 < 0) sec2 = one.search(/\n\s*Unit 2\s*:/i);
    if (sec2 < 0) sec2 = one.search(/\n\s*Unit 3\s*:/i);
    let sec3 = one.search(/\n\s*Summer Term\s*\n/i);
    if (sec3 < 0) sec3 = one.search(/\n\s*Unit 4\s*:/i);
    if (sec3 < 0) sec3 = one.search(/\n\s*Unit 5\s*:/i);
    if (sec2 >= 0 && sec3 > sec2) {
      html = toBr(one.slice(0, sec2).trim()) + hr + toBr(one.slice(sec2, sec3).trim()) + hr + toBr(one.slice(sec3).trim());
    } else if (sec2 >= 0) {
      html = toBr(one.slice(0, sec2).trim()) + hr + toBr(one.slice(sec2).trim()) + hr + toBr('Remaining units and lessons.');
    } else {
      html = toBr(one).replace(/(<br>){2,}/g, '<br><br>');
    }
  }
  return addLessonPlanLinks(addSchemeSpacers(html));
}

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [assets, setAssets] = useState([]);
  const [missions, setMissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const videoModalRef = useRef(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isReading, setIsReading] = useState(false);
  
  // Curriculum Upload state
  const [curriculumFile, setCurriculumFile] = useState(null);
  const [curriculumSubject, setCurriculumSubject] = useState('');
  const [curriculumGrade, setCurriculumGrade] = useState('');
  const [curriculumTopic, setCurriculumTopic] = useState('');
  const [curriculumLessonDescription, setCurriculumLessonDescription] = useState('');
  const [uploadingCurriculum, setUploadingCurriculum] = useState(false);
  const curriculumFileInputRef = useRef(null);
  const missionFormResetRef = useRef(null);

  // Custom subjects state
  const [availableSubjects, setAvailableSubjects] = useState(['Black History']);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [showAddSubjectInput, setShowAddSubjectInput] = useState(false);
  const [addingSubject, setAddingSubject] = useState(false);

  // Lesson selection state
  const [showLessonSelector, setShowLessonSelector] = useState(false);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);


  // Curriculum file handlers
  const handleCurriculumFileSelect = (file) => {
    // If file is an array, take the first file
    const actualFile = Array.isArray(file) ? file[0] : file;
    setCurriculumFile(actualFile);
  };

  const handleCurriculumFileRemove = () => {
    setCurriculumFile(null);
    if (curriculumFileInputRef.current) {
      curriculumFileInputRef.current.value = '';
    }
  };


  // Lesson selection handlers
  const handleFindLessons = () => {
    const lessons = getSelectableSubscriptionLessons(assets);
    setAvailableLessons(lessons);
    if (lessons.length === 0) {
      showNotification('Info', 'No lessons found. Please check your subscriptions.', 'info');
      return;
    }
    setShowLessonSelector(true);
  };

  const handleSelectLesson = (lesson) => {
    setCurriculumLessonDescription(lesson.description || '');
    setShowLessonSelector(false);
  };

  const handleCloseLessonSelector = () => {
    setShowLessonSelector(false);
  };
  
  // Matched Content state
  const [_matchedContent, _setMatchedContent] = useState([]);
  const [_filteredMatchedContent, _setFilteredMatchedContent] = useState([]);
  const [contentFilter, setContentFilter] = useState('all');
  const [latestCurriculumMatch, setLatestCurriculumMatch] = useState(null);
  // Curriculum cache state (Cache tab)
  const [cacheEntries, setCacheEntries] = useState([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheSubTab, setCacheSubTab] = useState('curriculum'); // 'curriculum' | 'lessonplans'
  const [showClearCurriculumCacheModal, setShowClearCurriculumCacheModal] = useState(false);
  const [showDeleteCurriculumCacheModal, setShowDeleteCurriculumCacheModal] = useState(false);
  const [curriculumCacheToDelete, setCurriculumCacheToDelete] = useState(null);
  // Lesson plan cache (Cache tab > Lesson plans)
  const [lessonPlanEntries, setLessonPlanEntries] = useState([]);
  const [lessonPlanCacheLoading, setLessonPlanCacheLoading] = useState(false);
  // Lesson plan modal (from scheme of work lesson breakdown links – AI-generated, or viewing saved)
  const [showLessonPlanModal, setShowLessonPlanModal] = useState(false);
  const [lessonPlanTitle, setLessonPlanTitle] = useState('');
  const [lessonPlanGenerated, setLessonPlanGenerated] = useState('');
  const [lessonPlanLoading, setLessonPlanLoading] = useState(false);
  const [lessonPlanFromCache, setLessonPlanFromCache] = useState(false);
  const [lessonPlanSubject, setLessonPlanSubject] = useState('');
  const [lessonPlanYearGroup, setLessonPlanYearGroup] = useState('');
  const [showDeleteLessonPlanModal, setShowDeleteLessonPlanModal] = useState(false);
  const [lessonPlanToDelete, setLessonPlanToDelete] = useState(null);
  const [showClearAllLessonPlansModal, setShowClearAllLessonPlansModal] = useState(false);
  const [_latestHistoricalContent, setLatestHistoricalContent] = useState(null);

  // Mission Form state
  const [showMissionForm, setShowMissionForm] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [missionToDelete, setMissionToDelete] = useState(null);
  
  // Mission Assignment state
  const [showMissionAssignModal, setShowMissionAssignModal] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [selectedStudentsForMissionAssignment, setSelectedStudentsForMissionAssignment] = useState([]);
  const [assignmentDueDate, setAssignmentDueDate] = useState('');
  const [assigningMission, setAssigningMission] = useState(false);
  
  
  // Lesson Assignment state
  const [_showLessonAssignModal, _setShowLessonAssignModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedStudentsForLessonAssignment, setSelectedStudentsForLessonAssignment] = useState([]);
  const [lessonAssignmentModalOpen, setLessonAssignmentModalOpen] = useState(false);
  const [selectedYearGroupForLesson, setSelectedYearGroupForLesson] = useState('all');
  const [lessonAssignmentNotes, setLessonAssignmentNotes] = useState('');
  const [assignedLessons, setAssignedLessons] = useState([]);
  const [loadingAssignedLessons, setLoadingAssignedLessons] = useState(false);
  const [assignedMissions, setAssignedMissions] = useState([]);
  const [loadingAssignedMissions, setLoadingAssignedMissions] = useState(false);
  const [assignedQuizzes, setAssignedQuizzes] = useState([]);
  const [loadingAssignedQuizzes, setLoadingAssignedQuizzes] = useState(false);
  const [showStudentWorkModal, setShowStudentWorkModal] = useState(false);
  const [studentWorkData, setStudentWorkData] = useState(null);
  const [_loadingStudentWork, setLoadingStudentWork] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignReason, setReassignReason] = useState('');
  const [reassigningMission, setReassigningMission] = useState(false);
  const [showEssayGrading, setShowEssayGrading] = useState(false);
  const [currentEssayQuestion, setCurrentEssayQuestion] = useState(null);
  const [essayGrade, setEssayGrade] = useState('');
  const [essayFeedback, setEssayFeedback] = useState('');
  const [gradingEssay, setGradingEssay] = useState(false);
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  
  // Quiz assignment delete confirmation state
  const [showQuizAssignmentDeleteModal, setShowQuizAssignmentDeleteModal] = useState(false);
  const [quizAssignmentToDelete, setQuizAssignmentToDelete] = useState(null);
  
  // Quiz assignment delete all confirmation state
  const [showQuizAssignmentDeleteAllModal, setShowQuizAssignmentDeleteAllModal] = useState(false);
  const [quizAssignmentToDeleteAll, setQuizAssignmentToDeleteAll] = useState(null);
  
  // Save to Library modal state
  const [showSaveToLibraryModal, setShowSaveToLibraryModal] = useState(false);
  const [contentToSave, setContentToSave] = useState(null);
  const [libraryFormData, setLibraryFormData] = useState({
    tags: '',
    category: 'academic',
    isPublic: false,
    isShared: false
  });

  // Grading state
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [gradingActivityType, setGradingActivityType] = useState(null);
  const [gradingActivityId, setGradingActivityId] = useState(null);
  const [gradingActivityName, setGradingActivityName] = useState('');
  const [gradingStudents, setGradingStudents] = useState([]);

  // Mission Progress Monitoring state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [monitoringMission, setMonitoringMission] = useState(null);
  const [showStudentProgressModal, setShowStudentProgressModal] = useState(false);
  const [monitoringStudent, setMonitoringStudent] = useState(null);
  const [showMissionViewerModal, setShowMissionViewerModal] = useState(false);
  const [viewingMission, setViewingMission] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSubscriptionExpiredModal, setShowSubscriptionExpiredModal] = useState(false);

  // Show subscription modal when: all subscriptions expired/cancelled, or no lessons available
  const subscriptionExpired = user?.subscriptions?.length > 0 &&
    user.subscriptions.every(s => s.status === 'expired' || s.status === 'cancelled');

  // Quiz and Library state
  const [showQuizCreationModal, setShowQuizCreationModal] = useState(false);
  const [_showMissionCreationModal, _setShowMissionCreationModal] = useState(false);
  const [curriculumData, setCurriculumData] = useState(null);

  // Student Management state
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    contactName: '',
    email: '',
    yearGrade: '',
    parentEmail: '',
    parentPhone: '',
    dateOfBirth: ''
  });
  const [bulkStudents, setBulkStudents] = useState([]);
  const [selectedCSVFile, setSelectedCSVFile] = useState(null);
  const [uploadingStudents, setUploadingStudents] = useState(false);
  const [generatedCSVData, setGeneratedCSVData] = useState(null);

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Bulk messaging state
  const [messageType, setMessageType] = useState('single'); // 'single', 'group', 'all'
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedYearGroupForMessage, setSelectedYearGroupForMessage] = useState('all');
  const [showDownloadCredentialsModal, setShowDownloadCredentialsModal] = useState(false);
  const [messageFromStudentCard, setMessageFromStudentCard] = useState(false);
  
  // Teacher Messages state
  const [sentMessages, setSentMessages] = useState([]);
  const [selectedSentMessage, setSelectedSentMessage] = useState(null);
  const [showSentMessageModal, setShowSentMessageModal] = useState(false);
  const [toast, setToast] = useState(null);

  // Calculate unread message count (messages with replies that haven't been viewed)
  const unreadMessageCount = sentMessages.filter(msg => msg.reply && !msg.viewed).length;

  // Year Group state
  const [selectedYearGroup, setSelectedYearGroup] = useState('all');
  const [yearGroups, setYearGroups] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(20);

  const handleSwitchToSchoolAdmin = async () => {
    try {
      // Update role in database
      const response = await axios.post(createApiUrl('/switch-role'), {
        role: 'schoolAdmin'
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
                // Refresh user data to get updated currentActiveRole
        try {
          const authResponse = await axios.get(createApiUrl('/auth/check'), {
            withCredentials: true
          });
          
          if (authResponse.data.user) {
            // Update the user in localStorage to ensure consistency
            localStorage.setItem('user', JSON.stringify(authResponse.data.user));
            // Force a page reload to ensure the AuthContext gets the updated user data
            window.location.href = '/school-admin-dashboard';
          } else {
            // Fallback to navigate if auth check fails
            navigate('/school-admin-dashboard');
          }
        } catch (authError) {
                    // Fallback to navigate if auth check fails
          navigate('/school-admin-dashboard');
        }
      } else {
                // Error handling removed for production
      }
    } catch (error) {
            // Error handling removed for production
    }
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    const messagesContainer = document.getElementById('messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  // Monitor lesson modal state changes

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'lessons', label: 'Lessons' },
    { id: 'assignedlessons', label: 'Assigned Lessons' },
    { id: 'missions', label: 'Missions' },
    { id: 'assignmissions', label: 'Assigned Missions' },
    { id: 'quizzes', label: 'Quizzes' },
    { id: 'assignedquizzes', label: 'Assigned Quizzes' },
    { id: 'library', label: 'Content Library' },
    { id: 'progress', label: 'Progress Tracking' },
    { id: 'students', label: 'Students' },
    { id: 'messages', label: 'Messages' },
    { id: 'curriculumupload', label: 'Curriculum Matcher' },
    { id: 'matchedcontent', label: 'Matched Content' },
    { id: 'lessonplans', label: 'Lesson plans' }
  ];

  const fetchCurriculumCache = async () => {
    setCacheLoading(true);
    try {
      const res = await axios.get(createApiUrl('/curriculum-cache'), { withCredentials: true });
      setCacheEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setCacheEntries([]);
    } finally {
      setCacheLoading(false);
    }
  };

  const clearCurriculumCache = async () => {
    try {
      await axios.delete(createApiUrl('/curriculum-cache'), { withCredentials: true });
      setCacheEntries([]);
      showToast('Cache cleared', 'success');
    } catch (err) {
      showToast('Failed to clear cache', 'error');
    }
  };

  const confirmClearCurriculumCache = async () => {
    await clearCurriculumCache();
    setShowClearCurriculumCacheModal(false);
  };

  const openDeleteCurriculumCacheModal = (e) => {
    setCurriculumCacheToDelete({ id: e.id, filename: e.filename || 'Document' });
    setShowDeleteCurriculumCacheModal(true);
  };

  const cancelDeleteCurriculumCache = () => {
    setShowDeleteCurriculumCacheModal(false);
    setCurriculumCacheToDelete(null);
  };

  const confirmDeleteCurriculumCache = async () => {
    if (!curriculumCacheToDelete) return;
    try {
      await axios.delete(createApiUrl(`/curriculum-cache/${curriculumCacheToDelete.id}`), { withCredentials: true });
      showToast('Cache entry deleted', 'success');
      fetchCurriculumCache();
      cancelDeleteCurriculumCache();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete cache entry', 'error');
    }
  };

  const fetchLessonPlanCache = async () => {
    try {
      setLessonPlanCacheLoading(true);
      const res = await axios.get(createApiUrl('/lesson-plan-cache'), { withCredentials: true });
      setLessonPlanEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load lesson plans', 'error');
      setLessonPlanEntries([]);
    } finally {
      setLessonPlanCacheLoading(false);
    }
  };

  const clearLessonPlanCache = async () => {
    try {
      await axios.delete(createApiUrl('/lesson-plan-cache'), { withCredentials: true });
      setLessonPlanEntries([]);
      showToast('Lesson plan cache cleared', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to clear cache', 'error');
    }
  };

  const confirmClearAllLessonPlans = async () => {
    await clearLessonPlanCache();
    setShowClearAllLessonPlansModal(false);
  };

  const openDeleteLessonPlanModal = (e) => {
    setLessonPlanToDelete({ id: e.id, lessonTitle: e.lessonTitle || 'Lesson plan' });
    setShowDeleteLessonPlanModal(true);
  };

  const cancelDeleteLessonPlan = () => {
    setShowDeleteLessonPlanModal(false);
    setLessonPlanToDelete(null);
  };

  const confirmDeleteLessonPlan = async () => {
    if (!lessonPlanToDelete) return;
    try {
      await axios.delete(createApiUrl(`/lesson-plan-cache/${lessonPlanToDelete.id}`), { withCredentials: true });
      showToast('Lesson plan deleted', 'success');
      fetchLessonPlanCache();
      cancelDeleteLessonPlan();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete lesson plan', 'error');
    }
  };

  const handleViewLessonPlan = async (id) => {
    try {
      setLessonPlanLoading(true);
      const res = await axios.get(createApiUrl(`/lesson-plan-cache/${id}`), { withCredentials: true });
      setLessonPlanTitle(res.data?.lessonTitle || 'Lesson plan');
      setLessonPlanGenerated(res.data?.lessonPlan || '');
      setLessonPlanSubject(res.data?.subject || '');
      setLessonPlanYearGroup(res.data?.yearGroup || '');
      setLessonPlanFromCache(true);
      setLessonPlanLoading(false);
      setShowLessonPlanModal(true);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load lesson plan', 'error');
      setLessonPlanLoading(false);
    }
  };

  const handleSchemeContentClick = async (e) => {
    const link = e.target.closest('.lesson-plan-link');
    if (!link) return;
    e.preventDefault();
    const title = link.getAttribute('data-lesson-title') || link.textContent || '';
    setLessonPlanTitle(title);
    setLessonPlanGenerated('');
    setLessonPlanSubject(curriculumSubject || '');
    setLessonPlanYearGroup(curriculumGrade || '');
    setLessonPlanFromCache(false);
    setShowLessonPlanModal(true);
    setLessonPlanLoading(true);
    try {
      const res = await axios.post(createApiUrl('/generate-lesson-plan'), {
        lessonTitle: title,
        subject: curriculumSubject || '',
        yearGroup: curriculumGrade || ''
      }, { withCredentials: true });
      setLessonPlanGenerated(res.data?.lessonPlan || '');
      setLessonPlanFromCache(res.data?.fromCache === true);
      fetchLessonPlanCache();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to generate lesson plan', 'error');
      setLessonPlanGenerated('');
    } finally {
      setLessonPlanLoading(false);
    }
  };

  const _handleCopyLessonPlan = () => {
    if (!lessonPlanGenerated) return;
    navigator.clipboard.writeText(lessonPlanGenerated).then(() => showToast('Lesson plan copied to clipboard', 'success')).catch(() => showToast('Copy failed', 'error'));
  };

  const handlePrintLessonPlan = () => {
    if (!lessonPlanTitle || !lessonPlanGenerated) return;
    const w = window.open('', '_blank');
    if (!w) {
      showToast('Please allow popups to print', 'error');
      return;
    }
    const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    w.document.write(`
      <!DOCTYPE html><html><head><title>Lesson plan: ${escapeHtml(lessonPlanTitle)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: system-ui, sans-serif; max-width: 100%; margin: 1rem auto; padding: 1rem 2rem; line-height: 1.6; color: #222; }
        .print-content { white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; max-width: 100%; overflow-x: hidden; }
        h1 { font-size: 1.5rem; margin-bottom: 1rem; word-wrap: break-word; overflow-wrap: break-word; }
        @media print {
          body { max-width: 100%; padding: 0.5in; }
          .print-content { white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; }
        }
      </style></head>
      <body><h1>Lesson plan: ${escapeHtml(lessonPlanTitle)}</h1><div class="print-content">${escapeHtml(lessonPlanGenerated)}</div></body></html>
    `);
    w.document.close();
    w.focus();
    w.onload = () => { w.print(); w.close(); };
  };

  const _handleSaveLessonPlan = async () => {
    if (!lessonPlanTitle || !lessonPlanGenerated) return;
    try {
      await axios.post(createApiUrl('/lesson-plan-cache'), {
        lessonTitle: lessonPlanTitle,
        subject: lessonPlanSubject || curriculumSubject || '',
        yearGroup: lessonPlanYearGroup || curriculumGrade || '',
        lessonPlan: lessonPlanGenerated
      }, { withCredentials: true });
      showToast('Saved to lesson plans', 'success');
      fetchLessonPlanCache();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save lesson plan', 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 'cache') {
      fetchCurriculumCache();
      if (cacheSubTab === 'lessonplans') fetchLessonPlanCache();
    }
    if (activeTab === 'lessonplans') fetchLessonPlanCache();
    const subscriptionLessons = getSubscriptionLessons(assets);
    const anyAssetExpired = subscriptionLessons.some(
      (a) => a.isInactive || a.subscriptionStatus === 'expired'
    );
    if (
      activeTab === 'lessons' &&
      (subscriptionExpired || subscriptionLessons.length === 0 || anyAssetExpired)
    ) {
      setShowSubscriptionExpiredModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cacheSubTab/fetchLessonPlanCache intentionally excluded
  }, [activeTab, subscriptionExpired, assets]);

  useEffect(() => {
    if (activeTab === 'cache' && cacheSubTab === 'lessonplans') fetchLessonPlanCache();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchLessonPlanCache intentionally excluded
  }, [cacheSubTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(createApiUrl('/teacher/dashboard'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      if (response.data.success) {
        const { user, curriculumTopics, missions, students } = response.data.data;
        
        setUser(user);
        
        // Get user's assets and curriculum topics for lessons
        const userAssets = user.assets || [];
        
        // Organize students by year groups
          const organizeStudentsByYearGroups = (studentsList) => {
          const groups = {};
          const uniqueGrades = new Set();

          studentsList.forEach(student => {
            const _grade = student.yearGrade || student.grade || student.Grade || student.yearGroup || '';
            uniqueGrades.add(_grade);

            if (!groups[_grade]) {
              groups[_grade] = [];
            }
            groups[_grade].push(student);
          });
          
          // Create year groups array for dropdown
          const yearGroupsList = Array.from(uniqueGrades).sort((a, b) => {
            if (a === '') return 1; // Put empty grades at the end
            if (a === 'K') return -1;
            if (b === 'K') return 1;
            return parseInt(a) - parseInt(b);
          });
          
          return { groups, yearGroupsList };
        };
        
        if (students.length > 0) {
        }
        
        const { groups: _groups, yearGroupsList } = organizeStudentsByYearGroups(students);
        setYearGroups(yearGroupsList);
        
        const lessonAssets = curriculumTopics ? curriculumTopics.map(topic => ({
          _id: topic._id,
          name: topic.topic,
          description: `Academic Subject: ${topic.academicSubject}, Grade Level: ${topic.gradeLevel}`,
          category: topic.academicSubject,
          matchedContent: topic.matchedContent,
          type: 'curriculum'
        })) : [];
        
        // Combine user assets and curriculum topics
        const allAssets = [...userAssets, ...lessonAssets];
        
        // Set assets - teachers should get real data from their school/organization
        setAssets(allAssets);

        // Fetch missions separately to ensure we get the latest data
        try {
          const missionsResponse = await axios.get(createApiUrl('/missions'), { withCredentials: true });
          if (missionsResponse.data.success) {
            setMissions(missionsResponse.data.data);
          } else {
            setMissions(missions || []);
          }
        } catch (missionsError) {
          setMissions(missions || []);
        }
        setStudents(students || []);
        
        // Fetch sent messages
        try {
          const messagesResponse = await axios.get(createApiUrl('/teacher/messages'));
          if (messagesResponse.data.success) {
            setSentMessages(messagesResponse.data.data);
          }
        } catch (messagesError) {
          setSentMessages([]); // Set empty array so dashboard still works
        }
      } else {
        setError('Failed to load dashboard data');
      }
      
      setLoading(false);
    } catch (error) {
      // console.error('TeacherDashboard fetchData error:', error);
      // console.error('Error response:', error.response?.data);
      // console.error('Error status:', error.response?.status);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      setLoading(false);
      
      // Set fallback data to prevent complete failure
      setAssets([]);
      setMissions([]);
      setStudents([]);
    }
  };

  // Fetch custom subjects on component mount
  const fetchSubjects = async () => {
    try {
      const response = await axios.get(createApiUrl('/teacher/subjects'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAvailableSubjects(response.data.data || ['Black History']);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Keep default subjects on error
      setAvailableSubjects(['Black History']);
    }
  };

  // Add new subject
  const handleAddSubject = async () => {
    if (!newSubjectName.trim()) {
      return;
    }
    
    setAddingSubject(true);
    try {
      const response = await axios.post(createApiUrl('/teacher/subjects'), {
        subjectName: newSubjectName.trim()
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Refresh subjects list
        await fetchSubjects();
        // Set the newly added subject as selected
        setCurriculumSubject(newSubjectName.trim());
        // Clear input and hide add input
        setNewSubjectName('');
        setShowAddSubjectInput(false);
      }
    } catch (error) {
      console.error('Error adding subject:', error);
      showToast('Failed to add subject. Please try again.', 'error');
    } finally {
      setAddingSubject(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSubjects();
  }, []);

  // Interactive handlers
  const handleVideoClick = (asset) => {
    const playback = getAssetVideoPlayback(asset);
    if (!playback?.url) return;
    setSelectedVideo(playback);
    setShowVideoModal(true);
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleStudentSelectionForLessonAssignment = (student) => {
    setSelectedStudentsForLessonAssignment(prev => {
      const isSelected = prev.find(s => s._id === student._id);
      if (isSelected) {
        return prev.filter(s => s._id !== student._id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleAssignLessonToStudents = async () => {
    if (selectedStudentsForLessonAssignment.length === 0) {
      showToast('Please select at least one student to assign the lesson to.', 'error');
      return;
    }

    try {
      const response = await axios.post(createApiUrl('/lesson-assignments'), {
        lessonId: selectedLesson._id,
        studentIds: selectedStudentsForLessonAssignment.map(s => s._id),
        notes: lessonAssignmentNotes.trim() || undefined
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        showToast(`Lesson "${selectedLesson?.name}" assigned to ${response.data.data.assigned} student(s)!`, 'success');
        setLessonAssignmentModalOpen(false);
        setSelectedLesson(null);
        setSelectedStudentsForLessonAssignment([]);
        setSelectedYearGroupForLesson('all');
        setLessonAssignmentNotes('');
        // Refresh assigned lessons list
        fetchAssignedLessons();
      } else {
        showToast('Failed to assign lesson. Please try again.', 'error');
      }
    } catch (error) {
      // console.error('Error assigning lesson:', error);
      showToast('Failed to assign lesson. Please try again.', 'error');
    }
  };

  const handleCancelLessonAssignment = () => {
    setLessonAssignmentModalOpen(false);
    setSelectedLesson(null);
    setSelectedStudentsForLessonAssignment([]);
    setSelectedYearGroupForLesson('all');
    setLessonAssignmentNotes('');
  };

  const handleDeleteLessonAssignment = (assignmentId, lessonName, studentName) => {
    setAssignmentToDelete({ assignmentId, lessonName, studentName });
    setShowDeleteModal(true);
  };

  const confirmDeleteAssignment = async () => {
    if (!assignmentToDelete) return;

    try {
      if (assignmentToDelete.isAllForLesson) {
        // Delete all assignments for this lesson
        const lessonAssignments = assignedLessons.filter(a => a.lessonId?._id === assignmentToDelete.lessonId);
        const deletePromises = lessonAssignments.map(assignment => 
          axios.delete(createApiUrl(`/api/lesson-assignments/${assignment._id}`), {
            withCredentials: true
          })
        );
        
        await Promise.all(deletePromises);
        showToast(`All assignments for "${assignmentToDelete.lessonName}" deleted successfully!`, 'success');
      } else {
        // Delete single assignment
        const response = await axios.delete(createApiUrl(`/api/lesson-assignments/${assignmentToDelete.assignmentId}`), {
          withCredentials: true
        });

        if (response.data.success) {
          showToast(`Lesson assignment deleted successfully!`, 'success');
        } else {
          showToast('Failed to delete lesson assignment. Please try again.', 'error');
          return;
        }
      }
      
      fetchAssignedLessons(); // Refresh the list
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    } catch (error) {
      // console.error('Error deleting lesson assignment:', error);
      showToast('Failed to delete lesson assignment. Please try again.', 'error');
    }
  };

  const cancelDeleteAssignment = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
  };

  // Grading functions

  const handleGradeLesson = (lessonGroup) => {
    setGradingActivityType('lesson');
    setGradingActivityId(lessonGroup.lessonId);
    setGradingActivityName(lessonGroup.lessonName);
    
    // Check if assignments exists and is an array before mapping
    const assignments = lessonGroup.assignments || [];
    setGradingStudents(assignments.map(a => ({
      _id: a.studentId._id,
      contactName: a.studentId.contactName,
      firstName: a.studentId.firstName,
      lastName: a.studentId.lastName,
      yearGrade: a.studentId.yearGrade,
      assignmentId: a._id
    })));
    setShowGradingModal(true);
  };

  const handleGradeMission = (missionGroup) => {
    setGradingActivityType('mission');
    setGradingActivityId(missionGroup.missionId);
    setGradingActivityName(missionGroup.missionName);
    
    // Check if assignments exists and is an array before mapping
    const assignments = missionGroup.assignments || [];
    setGradingStudents(assignments.map(a => ({
      _id: a.studentId._id,
      contactName: a.studentId.contactName,
      firstName: a.studentId.firstName,
      lastName: a.studentId.lastName,
      yearGrade: a.studentId.yearGrade,
      assignmentId: a._id
    })));
    setShowGradingModal(true);
  };

  const handleViewStudentWork = async (assignmentId, type = 'mission') => {
    setLoadingStudentWork(true);
    try {
      const endpoint = type === 'quiz' 
        ? `/api/quiz-assignments/${assignmentId}/submission`
        : `/api/mission-assignments/${assignmentId}/submission`;
        
      const response = await axios.get(createApiUrl(endpoint), {
        withCredentials: true
      });
      
      if (response.data.success) {
        setStudentWorkData(response.data.data);
        setShowStudentWorkModal(true);
      } else {
        showNotification('Error', 'Failed to load student work', 'error');
      }
    } catch (error) {
      // console.error('Error fetching student work:', error);
      showNotification('Error', 'Error loading student work', 'error');
    } finally {
      setLoadingStudentWork(false);
    }
  };

  const handleReassignMission = async () => {
    if (!reassignReason.trim()) {
      showNotification('Validation Error', 'Please provide a reason for re-assigning this mission', 'warning');
      return;
    }

    setReassigningMission(true);
    try {
      const response = await axios.post(createApiUrl(`/api/mission-assignments/${studentWorkData.assignment._id}/reassign`), {
        reason: reassignReason
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        showToast('Mission re-assigned successfully!', 'success');
        setShowReassignModal(false);
        setShowStudentWorkModal(false);
        setReassignReason('');
        // Refresh the assigned missions
        fetchAssignedMissions();
      } else {
        showNotification('Error', 'Failed to re-assign mission', 'error');
      }
    } catch (error) {
      // console.error('Error re-assigning mission:', error);
      showNotification('Error', 'Error re-assigning mission', 'error');
    } finally {
      setReassigningMission(false);
    }
  };

  const handleGradeEssay = (question, studentAnswer) => {
    setCurrentEssayQuestion({
      question: question,
      studentAnswer: studentAnswer,
      questionIndex: studentWorkData.quiz.questions.findIndex(q => q._id === question._id)
    });
    setEssayGrade('');
    setEssayFeedback('');
    setShowEssayGrading(true);
  };

  const handleSubmitEssayGrade = async () => {
    if (!essayGrade || isNaN(essayGrade) || essayGrade < 0 || essayGrade > 100) {
      showNotification('Validation Error', 'Please enter a valid grade between 0 and 100', 'warning');
      return;
    }

    setGradingEssay(true);
    try {
      // Update the quiz assignment with the essay grade
      const response = await axios.put(
        createApiUrl(`/api/quiz-assignments/${studentWorkData.assignment._id}/status`),
        {
          status: 'graded',
          notes: essayFeedback,
          essayGrades: {
            [currentEssayQuestion.question._id]: {
              grade: parseInt(essayGrade),
              feedback: essayFeedback,
              gradedAt: new Date().toISOString()
            }
          }
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        showToast('Essay graded successfully!', 'success');
        setShowEssayGrading(false);
        setCurrentEssayQuestion(null);
        setEssayGrade('');
        setEssayFeedback('');
        
        // Refresh the student work data
        handleViewStudentWork(studentWorkData.assignment._id, 'quiz');
      } else {
        showNotification('Error', 'Failed to grade essay', 'error');
      }
    } catch (error) {
      console.error('Error grading essay:', error);
      showNotification('Error', 'Error grading essay', 'error');
    } finally {
      setGradingEssay(false);
    }
  };

  const handleGradeSubmitted = (_grade) => {
    showToast('Grade saved successfully!', 'success');
    // Refresh data if needed
    if (gradingActivityType === 'mission') {
      fetchMissions();
      fetchAssignedMissions();
    } else {
      fetchAssignedLessons();
    }
  };

  const closeGradingModal = () => {
    setShowGradingModal(false);
    setGradingActivityType(null);
    setGradingActivityId(null);
    setGradingActivityName('');
    setGradingStudents([]);
  };

  // Fetch missions function
  const fetchMissions = async () => {
    try {
      const response = await axios.get(createApiUrl('/missions'), { withCredentials: true });
      if (response.data.success) {
        setMissions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching missions:', error);
    }
  };

  // Group assignments by lesson
  const getGroupedAssignments = () => {
    const grouped = {};
    
    assignedLessons.forEach(assignment => {
      const lessonId = assignment.lessonId?._id || 'unknown';
      
      if (!grouped[lessonId]) {
        grouped[lessonId] = {
          lessonId: assignment.lessonId?._id,
          lessonName: assignment.lessonId?.name || '',
          lessonDescription: assignment.lessonId?.description,
          assignments: [],
          earliestAssignment: assignment.assignedDate,
          statusCounts: {
            completed: 0,
            in_progress: 0,
            assigned: 0,
            overdue: 0
          }
        };
      }
      
      grouped[lessonId].assignments.push(assignment);
      
      // Update earliest assignment date
      if (new Date(assignment.assignedDate) < new Date(grouped[lessonId].earliestAssignment)) {
        grouped[lessonId].earliestAssignment = assignment.assignedDate;
      }
      
      // Count statuses
      grouped[lessonId].statusCounts[assignment.status] = (grouped[lessonId].statusCounts[assignment.status] || 0) + 1;
    });
    
    return Object.values(grouped);
  };

  const getGroupedMissionAssignments = () => {
    const grouped = {};
    
    assignedMissions.forEach(assignment => {
      const missionId = assignment.missionId?._id || 'unknown';
      
      if (!grouped[missionId]) {
        grouped[missionId] = {
          missionId: assignment.missionId?._id,
          missionName: assignment.missionId?.title || '',
          missionDescription: assignment.missionId?.description,
          assignments: [],
          earliestAssignment: assignment.assignedAt,
          statusCounts: {
            completed: 0,
            in_progress: 0,
            assigned: 0,
            overdue: 0
          }
        };
      }
      
      grouped[missionId].assignments.push(assignment);
      
      // Update earliest assignment date
      if (new Date(assignment.assignedAt) < new Date(grouped[missionId].earliestAssignment)) {
        grouped[missionId].earliestAssignment = assignment.assignedAt;
      }
      
      // Count statuses
      grouped[missionId].statusCounts[assignment.status] = (grouped[missionId].statusCounts[assignment.status] || 0) + 1;
    });
    
    return Object.values(grouped);
  };

  const getGroupedQuizAssignments = () => {
    const grouped = {};
    
    assignedQuizzes.forEach(assignment => {
      const quizId = assignment.quizId?._id || 'unknown';
      
      if (!grouped[quizId]) {
        grouped[quizId] = {
          quizId: assignment.quizId?._id,
          quizName: assignment.quizId?.title || '',
          quizDescription: assignment.quizId?.description,
          assignments: [],
          earliestAssignment: assignment.assignedDate,
          statusCounts: {
            completed: 0,
            in_progress: 0,
            assigned: 0,
            overdue: 0
          }
        };
      }
      
      grouped[quizId].assignments.push(assignment);
      
      // Update earliest assignment date
      if (new Date(assignment.assignedDate) < new Date(grouped[quizId].earliestAssignment)) {
        grouped[quizId].earliestAssignment = assignment.assignedDate;
      }
      
      // Count statuses
      grouped[quizId].statusCounts[assignment.status] = (grouped[quizId].statusCounts[assignment.status] || 0) + 1;
    });
    
    return Object.values(grouped);
  };

  // Delete all assignments for a specific lesson
  const handleDeleteAllAssignmentsForLesson = (lessonId, lessonName) => {
    const lessonAssignments = assignedLessons.filter(a => a.lessonId?._id === lessonId);
    const studentNames = lessonAssignments.map(a => 
      a.studentId?.firstName && a.studentId?.lastName 
        ? `${a.studentId.firstName} ${a.studentId.lastName}`
        : a.studentId?.contactName || ''
    ).join(', ');
    
    setAssignmentToDelete({ 
      assignmentId: 'all', 
      lessonName, 
      studentName: `${lessonAssignments.length} students (${studentNames})`,
      isAllForLesson: true,
      lessonId
    });
    setShowDeleteModal(true);
  };

  // Helper function to check if all students in the current filter are selected
  const isAllStudentsSelected = () => {
    const filteredStudents = getFilteredStudents().filter(student => {
      const isTeacherStudent = student.teacherCode === user?.teacherCode;
      const matchesYearGroup = selectedYearGroupForLesson === 'all' || 
        (student.yearGrade || student.grade || student.Grade || student.yearGroup || '') === selectedYearGroupForLesson;
      return isTeacherStudent && matchesYearGroup;
    });

    if (filteredStudents.length === 0) return false;
    
    return filteredStudents.every(student => 
      selectedStudentsForLessonAssignment.find(s => s._id === student._id) !== undefined
    );
  };

  // Handle select all students
  const handleSelectAllStudents = () => {
    const filteredStudents = getFilteredStudents().filter(student => {
      const isTeacherStudent = student.teacherCode === user?.teacherCode;
      const matchesYearGroup = selectedYearGroupForLesson === 'all' || 
        (student.yearGrade || student.grade || student.Grade || student.yearGroup || '') === selectedYearGroupForLesson;
      return isTeacherStudent && matchesYearGroup;
    });

    if (isAllStudentsSelected()) {
      // If all are selected, deselect all in this filter
      const filteredStudentIds = filteredStudents.map(s => s._id);
      setSelectedStudentsForLessonAssignment(prev => 
        prev.filter(student => !filteredStudentIds.includes(student._id))
      );
    } else {
      // If not all are selected, select all in this filter
      const newSelections = filteredStudents.filter(student => 
        !selectedStudentsForLessonAssignment.find(s => s._id === student._id)
      );
      setSelectedStudentsForLessonAssignment(prev => [...prev, ...newSelections]);
    }
  };

  const handleAssignLesson = (asset) => {
    setSelectedLesson(asset);
    setSelectedStudentsForLessonAssignment([]);
    setSelectedYearGroupForLesson('all');
    setLessonAssignmentNotes('');
    setLessonAssignmentModalOpen(true);
  };

  // Fetch assigned lessons
  const fetchAssignedLessons = async () => {
    setLoadingAssignedLessons(true);
    try {
      const response = await axios.get(createApiUrl('/lesson-assignments/teacher'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAssignedLessons(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching assigned lessons:', error);
      
    } finally {
      setLoadingAssignedLessons(false);
    }
  };

  const fetchAssignedMissions = async () => {
    setLoadingAssignedMissions(true);
    try {
      const response = await axios.get(createApiUrl('/mission-assignments/teacher'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAssignedMissions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching assigned missions:', error);
    } finally {
      setLoadingAssignedMissions(false);
    }
  };

  const fetchAssignedQuizzes = async () => {
    setLoadingAssignedQuizzes(true);
    try {
      const response = await axios.get(createApiUrl('/quiz-assignments/teacher'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAssignedQuizzes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching assigned quizzes:', error);
    } finally {
      setLoadingAssignedQuizzes(false);
    }
  };

  const closeVideoModal = () => {
    if (videoModalRef.current) {
      videoModalRef.current.pause();
      videoModalRef.current.removeAttribute('src');
      videoModalRef.current.load();
    }
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  // AI Agent handlers
  const _handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles(files);
  };

  const handleAIFileSelect = (files) => {
    setUploadedFiles(files);
  };

  const handleAIFileRemove = () => {
    setUploadedFiles([]);
  };

  // Helper function for generating usernames
  const _generateUsername = (firstName, lastName) => {
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toLowerCase();
    const randomNumber = Math.floor(1000000 + Math.random() * 9000000); // 7-digit number
    return `${initials}${randomNumber}`;
  };

  const getStudentGrade = (student) => {
    if (!student) return '';
    return String(
      student.yearGrade || student.grade || student.Grade || student.yearGroup || ''
    ).trim();
  };

  const parseCsvLine = (line, delimiter = ',') => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delimiter && !inQuotes) {
        result.push(current.trim().replace(/^['"]|['"]$/g, ''));
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim().replace(/^['"]|['"]$/g, ''));
    return result;
  };

  const optionalCsvValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    if (/^(n\/a|na|none|null|undefined|-)$/i.test(raw)) return '';
    return raw;
  };

  const normalizeCsvHeader = (header) =>
    String(header || '')
      .replace(/^\uFEFF/, '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '');

  // Transform grade values to match stored yearGrade (e.g. "Year 4", "Grade 3", "4")
  const transformGrade = (grade) => {
    if (grade == null) return '';
    let gradeStr = grade.toString().replace(/^\uFEFF/, '').trim();
    if (!gradeStr) return '';

    gradeStr = gradeStr.replace(/^["']|["']$/g, '').trim();
    const lower = gradeStr.toLowerCase();

    if (lower.startsWith('year')) {
      return gradeStr.replace(/^year/i, '').trim();
    }
    if (lower.startsWith('grade')) {
      return gradeStr.replace(/^grade/i, '').trim();
    }
    if (lower.startsWith('yr')) {
      return gradeStr.replace(/^yr/i, '').trim();
    }
    const gMatch = lower.match(/^g\s*(\d+)$/);
    if (gMatch) {
      return gMatch[1];
    }
    if (/^(k|r|reception)$/i.test(gradeStr)) {
      return 'K';
    }
    if (/^\d+$/.test(gradeStr)) {
      return gradeStr;
    }
    return gradeStr;
  };

  // CSV file handlers for drag-drop component
  const handleCSVFileSelect = (files) => {
    // DragDropFileUpload passes an array, so get the first file
    const file = Array.isArray(files) ? files[0] : files;
    
    if (file) {
      setSelectedCSVFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = String(e.target.result || '').replace(/^\uFEFF/, '');
          const lines = csv.split(/\r?\n/).filter((line) => line.trim());
          const students = [];
          const csvData = [['First Name', 'Last Name', 'Email', 'Grade', 'Parent Email', 'Parent Phone']];

          if (lines.length < 2) {
            setBulkStudents([]);
            setGeneratedCSVData(csvData);
            return;
          }

          const headerLine = lines[0];
          const delimiter = (headerLine.match(/;/g) || []).length > (headerLine.match(/,/g) || []).length
            ? ';'
            : ',';
          const headers = parseCsvLine(headerLine, delimiter).map(normalizeCsvHeader);
          const columnMap = {
            firstname: 'firstName',
            lastname: 'lastName',
            surname: 'lastName',
            email: 'email',
            grade: 'yearGrade',
            year: 'yearGrade',
            yeargrade: 'yearGrade',
            yeargroup: 'yearGrade',
            parentemail: 'parentEmail',
            parentphone: 'parentPhone',
          };
          const fieldIndexes = {};
          headers.forEach((header, index) => {
            const field = columnMap[header];
            if (field && fieldIndexes[field] == null) {
              fieldIndexes[field] = index;
            }
          });
          if (fieldIndexes.firstName == null) fieldIndexes.firstName = 0;
          if (fieldIndexes.lastName == null) fieldIndexes.lastName = 1;
          if (fieldIndexes.email == null) fieldIndexes.email = 2;
          if (fieldIndexes.yearGrade == null) fieldIndexes.yearGrade = 3;
          // Parent Email / Parent Phone are optional — only read them when those columns exist

          for (let i = 1; i < lines.length; i++) {
            const values = parseCsvLine(lines[i], delimiter);
            const firstName = optionalCsvValue(values[fieldIndexes.firstName]);
            const lastName = optionalCsvValue(values[fieldIndexes.lastName]);
            const fullName = `${firstName} ${lastName}`.trim();
            if (!fullName) continue;

            const yearGrade = transformGrade(values[fieldIndexes.yearGrade]);
            const student = {
              contactName: fullName,
              email: optionalCsvValue(values[fieldIndexes.email]),
              yearGrade,
              parentEmail: fieldIndexes.parentEmail == null
                ? ''
                : optionalCsvValue(values[fieldIndexes.parentEmail]),
              parentPhone: fieldIndexes.parentPhone == null
                ? ''
                : optionalCsvValue(values[fieldIndexes.parentPhone]),
            };
            students.push(student);
            csvData.push([
              firstName,
              lastName,
              student.email,
              yearGrade,
              student.parentEmail,
              student.parentPhone,
            ]);
          }

          setBulkStudents(students);
          setGeneratedCSVData(csvData);
        } catch (error) {
          console.error('Error parsing student CSV:', error);
          showToast('Could not read the CSV file. Check the column headers include Grade.', 'error');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleCSVFileRemove = () => {
    setBulkStudents([]);
    setSelectedCSVFile(null);
    setGeneratedCSVData(null);
  };

  // Function to download the generated CSV with usernames and passwords
  const downloadGeneratedCSV = () => {
    if (!generatedCSVData) return;

    // Convert array to CSV string
    const csvContent = generatedCSVData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'students_with_credentials.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to download all current students' credentials
  const downloadAllStudentsCSV = () => {
    if (!students || students.length === 0) {
      showToast('No students found to download.', 'error');
      return;
    }

    // Generate CSV data from current students
    const csvData = [
      ['Name', 'Email', 'Grade', 'Username', 'Password', 'Student Code']
    ];
    
    students.forEach(student => {
      csvData.push([
        student.contactName,
        student.email || 'No email provided',
        getStudentGrade(student),
        student.username,
        student.displayPassword || student.password || 'Password not available',
        student.studentCode
      ]);
    });

    // Convert array to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'all_students_credentials.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setShowDownloadCredentialsModal(false);
    showToast(`Downloaded credentials for ${students.length} students.`, 'success');
  };

  // Function to download students' credentials by year group
  const downloadStudentsByYearGroup = (yearGroup) => {
    if (!students || students.length === 0) {
      showToast('No students found to download.', 'error');
      return;
    }

    const filteredStudents = students.filter(student => getStudentGrade(student) === yearGroup);
    
    if (filteredStudents.length === 0) {
      showToast(`No students found in year group ${yearGroup}.`, 'error');
      return;
    }

    // Generate CSV data from filtered students
    const csvData = [
      ['Name', 'Email', 'Grade', 'Username', 'Password', 'Student Code']
    ];
    
    filteredStudents.forEach(student => {
      csvData.push([
        student.contactName,
        student.email || 'No email provided',
        getStudentGrade(student),
        student.username,
        student.displayPassword || student.password || 'Password not available',
        student.studentCode
      ]);
    });

    // Convert array to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `year_${yearGroup}_students_credentials.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setShowDownloadCredentialsModal(false);
    showToast(`Downloaded credentials for ${filteredStudents.length} students in ${getYearGroupDisplayName(yearGroup) || `year ${yearGroup}`}.`, 'success');
  };

  const sendMessage = async () => {
    if (!userInput.trim() && uploadedFiles.length === 0) {
      // Error handling removed for production
      return;
    }

    // Add user message immediately for better UX
    const userMessage = { type: 'user', content: userInput };
    setMessages(prev => [...prev, userMessage]);
    
    const currentInput = userInput;
    setUserInput(''); // Clear input immediately

    const formData = new FormData();
    formData.append('message', currentInput);
    uploadedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      
      // Use the existing aiAgent endpoint
      const response = await axios.post(createApiUrl('/ai-agent'), formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Try to extract the message content from various possible structures
      let messageContent = null;
      
      if (response.data && response.data.message) {
        messageContent = response.data.message;
      } else if (response.data && typeof response.data === 'string') {
        messageContent = response.data;
      } else if (response.data && response.data.response) {
        messageContent = response.data.response;
      } else if (response.data && response.data.content) {
        messageContent = response.data.content;
      } else if (response.data && response.data.text) {
        messageContent = response.data.text;
      } else if (response.data && response.data.answer) {
        messageContent = response.data.answer;
      } else if (response.data && response.data.reply) {
        messageContent = response.data.reply;
      } else if (response.data && response.data.result) {
        messageContent = response.data.result;
      } else if (response.data && response.data.data) {
        messageContent = response.data.data;
      }
      
      if (messageContent) {
        // Format the message content for better readability
        const formattedContent = typeof messageContent === 'string' 
          ? messageContent.replace(/\n/g, '<br>') 
          : messageContent;
        
        setMessages(prev => [...prev, { type: 'ai', content: formattedContent }]);
      } else {
        // If we can't find the message, show the full response for debugging
        const debugMessage = `Debug: Response received but couldn't extract message. Full response: ${JSON.stringify(response.data)}`;
        setMessages(prev => [...prev, { type: 'ai', content: debugMessage }]);
      }
      
      setUploadedFiles([]);
    } catch (error) {
                  // Show user-friendly error message
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      setMessages(prev => [...prev, { type: 'ai', content: `Error: ${errorMessage}` }]);
      
      // Restore the user input if there was an error
      setUserInput(currentInput);
    }
  };

  const startVoiceInput = async () => {
    // Check if speech recognition is supported
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    // Check if we're on HTTPS or localhost (required for microphone access)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      alert('Microphone access requires HTTPS. Please access this site via HTTPS.');
      return;
    }

    try {
      // Request microphone permission first
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
    } catch (permissionError) {
      if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
        alert('Microphone permission denied. Please allow microphone access in your browser settings and try again.');
        return;
      } else if (permissionError.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
        return;
      } else {
        console.error('Error requesting microphone permission:', permissionError);
      }
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setUserInput(transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      
      let errorMessage = 'Speech recognition error occurred. ';
      
      switch (event.error) {
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings and refresh the page.';
          break;
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again and speak clearly.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        case 'aborted':
          errorMessage = 'Speech recognition was aborted.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}. Please try again.`;
      }
      
      alert(errorMessage);
    };
    
    try {
      recognition.start();
    } catch (startError) {
      console.error('Error starting speech recognition:', startError);
      alert('Failed to start speech recognition. Please try again.');
    }
  };

  const _handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleReading = (e) => {
    // Prevent form submission
    e.preventDefault();
    e.stopPropagation();
    
    setIsReading(!isReading);
    if (!isReading) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.type === 'ai') {
        // Stop any existing speech first
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(lastMessage.content);
        utterance.onend = () => {
          setIsReading(false);
        };
        utterance.onerror = () => {
          setIsReading(false);
        };
        window.speechSynthesis.speak(utterance);
      }
    } else {
      window.speechSynthesis.cancel();
    }
  };

  const openMissionFormWithPrefill = (missionDetails, options = {}) => {
    
    // Clean the content of HTML tags before parsing
    const cleanedMissionDetails = (typeof missionDetails === 'string' ? missionDetails : '')
      .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to newlines
      .replace(/<\/?[^>]+(>|$)/g, '')  // Remove any other HTML tags
      .replace(/\n\s*\n/g, '\n\n')     // Clean up multiple newlines
      .trim();

    // Parse the cleaned mission details to extract title and resources
    const parsedData = parseMissionDetails(cleanedMissionDetails);
    if (options.subject !== undefined && options.subject !== '') parsedData.subject = options.subject;
    if (options.gradeLevel !== undefined && options.gradeLevel !== '') parsedData.gradeLevel = options.gradeLevel;
    if (options.title !== undefined && options.title !== '') parsedData.title = options.title;
    // When opening from lesson plan (title provided), use full content as the main body so the whole plan is sent
    if (options.title !== undefined && options.title !== '') parsedData.learningObjectives = cleanedMissionDetails;
    
    // Store the parsed data in localStorage for the mission form to access
    localStorage.setItem('prefillMission', JSON.stringify(parsedData));
    
    // Switch to Missions tab using state
    setActiveTab('missions');
    
    // Wait for tab switch and then open form
    setTimeout(() => {
      setShowMissionForm(true);
    }, 500); // Increased timeout to ensure tab switch completes
  };

  const parseMissionDetails = (missionDetails) => {
    
    const parsed = {
      title: '',
      description: '',
      resources: '',
      instructions: '',
      learningObjectives: '',
      materials: '',
      assessment: '',
      contribution: '',
      connection: '',
      blackHistoryConnections: ''
    };

    try {
      // Use the passed missionDetails parameter, with localStorage as fallback
      const contentToParse = missionDetails || localStorage.getItem('cleanMissionContent') || '';
      
      // Extract mission title - try multiple formats
      let titleMatch = contentToParse.match(/\*\*Mission Title:\*\*\s*(.+?)(?=\n|$)/i);
      if (!titleMatch) {
        // Try alternative formats
        titleMatch = contentToParse.match(/Mission Title:\s*(.+?)(?=\n|$)/i);
      }
      if (!titleMatch) {
        // Try to extract from the first line or topic header
        titleMatch = contentToParse.match(/^(?:Topic \d+:|Curriculum Analysis:|### \d+\.|## \d+\.|# \d+\.)\s*(.+?)(?=\n|$)/i);
      }
      if (titleMatch) {
        parsed.title = titleMatch[1].trim();
      }

      // Extract learning objective - try multiple formats
      let objectiveMatch = contentToParse.match(/\*\*Learning Objective:\*\*\s*([\s\S]*?)(?=\n\*\*|\n-|\n\n|$)/i);
      if (!objectiveMatch) {
        // Try alternative formats
        objectiveMatch = contentToParse.match(/Learning Objective:\s*([\s\S]*?)(?=\n\*\*|\n-|\n\n|$)/i);
      }
      if (!objectiveMatch) {
        objectiveMatch = contentToParse.match(/(?:Learning Objective|Objective|Goal):\s*([\s\S]*?)(?=\n\*\*|\n-|\n\n|$)/i);
      }
      if (objectiveMatch) {
        parsed.learningObjectives = objectiveMatch[1].trim();
      }

      // Extract instructions - stop at Materials Needed or Assessment
      let instructionsMatch = contentToParse.match(/\*\*Instructions:\*\*\s*([\s\S]*?)(?=\n\*\*Materials Needed|\n\*\*Assessment|\n\n|$)/i);
      if (!instructionsMatch) {
        instructionsMatch = contentToParse.match(/Instructions:\s*([\s\S]*?)(?=\n\*\*Materials Needed|\n\*\*Assessment|\n\n|$)/i);
      }
      if (instructionsMatch) {
        parsed.instructions = instructionsMatch[1].trim();
      }

      // Extract materials needed - stop at Assessment
      let materialsMatch = contentToParse.match(/\*\*Materials Needed:\*\*\s*([\s\S]*?)(?=\n\*\*Assessment|\n\n|$)/i);
      if (!materialsMatch) {
        materialsMatch = contentToParse.match(/Materials Needed:\s*([\s\S]*?)(?=\n\*\*Assessment|\n\n|$)/i);
      }
      if (materialsMatch) {
        parsed.materials = materialsMatch[1].trim();
      }

      // Extract assessment - stop at end or next major section
      let assessmentMatch = contentToParse.match(/\*\*Assessment:\*\*\s*([\s\S]*?)(?=\n\*\*Recommended Resources|\n\n|$)/i);
      if (!assessmentMatch) {
        assessmentMatch = contentToParse.match(/Assessment:\s*([\s\S]*?)(?=\n\*\*Recommended Resources|\n\n|$)/i);
      }
      if (assessmentMatch) {
        parsed.assessment = assessmentMatch[1].trim();
      }

      // Extract recommended resources - stop at next major section
      let resourcesMatch = contentToParse.match(/\*\*Recommended Resources:\*\*\s*([\s\S]*?)(?=\n\n|$)/i);
      if (!resourcesMatch) {
        resourcesMatch = contentToParse.match(/(?:Recommended Resources|Black History Connections):\s*([\s\S]*?)(?=\n\n|$)/i);
      }
      if (resourcesMatch) {
        parsed.resources = resourcesMatch[1].trim();
      }

      // Extract contribution/impact - look for it in the Black History Connections section
      let contributionMatch = contentToParse.match(/\*\*Contribution:\*\*\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      if (!contributionMatch) {
        // Try alternative formats
        contributionMatch = contentToParse.match(/Contribution:\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      }
      if (!contributionMatch) {
        contributionMatch = contentToParse.match(/(?:Contribution|Impact|Significance):\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      }
      if (contributionMatch) {
        parsed.contribution = contributionMatch[1].trim();
      }

      // Extract connection to topic - look for it in the Black History Connections section
      let connectionMatch = contentToParse.match(/\*\*Connection to Topic:\*\*\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      if (!connectionMatch) {
        // Try alternative formats
        connectionMatch = contentToParse.match(/Connection to Topic:\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      }
      if (!connectionMatch) {
        connectionMatch = contentToParse.match(/(?:Connection to Topic|Topic Connection|Relevance):\s*([\s\S]*?)(?=\n\s*\*\*|\n\s*-|\n\n|$)/i);
      }
      if (connectionMatch) {
        parsed.connection = connectionMatch[1].trim();
      }

      // Extract black history connections - stop at Instructions or next major section
      let blackHistoryMatch = contentToParse.match(/\*\*(?:Black History Connections?|Historical Connections?):\*\*\s*([\s\S]*?)(?=\n\*\*Instructions|\n\*\*Puzzle Mission|\n\n|$)/i);
      if (!blackHistoryMatch) {
        blackHistoryMatch = contentToParse.match(/(?:Black History Connections?|Historical Connections?):\s*([\s\S]*?)(?=\n\*\*Instructions|\n\*\*Puzzle Mission|\n\n|$)/i);
      }
      if (blackHistoryMatch) {
        parsed.blackHistoryConnections = blackHistoryMatch[1].trim();
      }

      // Create a clean description by combining the parsed sections
      const cleanDescription = [
        parsed.title && `# ${parsed.title}`,
        parsed.learningObjectives && `## Learning Objectives\n${parsed.learningObjectives}`,
        parsed.contribution && `## Contribution & Impact\n${parsed.contribution}`,
        parsed.connection && `## Connection to Topic\n${parsed.connection}`,
        parsed.blackHistoryConnections && `## Black History Connections\n${parsed.blackHistoryConnections}`,
        parsed.instructions && `## Instructions\n${parsed.instructions}`,
        parsed.materials && `## Materials Needed\n${parsed.materials}`,
        parsed.assessment && `## Assessment\n${parsed.assessment}`,
        parsed.resources && `## Resources\n${parsed.resources}`
      ].filter(Boolean).join('\n\n');
      
      // Create a clean description by combining the parsed sections
      if (cleanDescription && cleanDescription.length > 20) {
        parsed.description = cleanDescription;
      } else {
        // Fallback: use the full content if structured parsing didn't work
        const cleanedContent = contentToParse
          .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to newlines
          .replace(/<\/?[^>]+(>|$)/g, '')  // Remove any other HTML tags
          .replace(/\n\s*\n/g, '\n\n')     // Clean up multiple newlines
          .trim();
        parsed.description = cleanedContent;
      }

      // If we couldn't parse structured content, use the full content for key fields
      if (!parsed.title && contentToParse) {
        // Try to extract a title from the first meaningful line
        const lines = contentToParse.split('\n').filter(line => line.trim().length > 0);
        if (lines.length > 0) {
          parsed.title = lines[0].replace(/^[#\s]*/, '').trim();
        }
      }
      
      if (!parsed.learningObjectives && contentToParse) {
        // Use the full content as learning objectives if nothing specific was found
        parsed.learningObjectives = contentToParse.trim();
      }

    } catch (error) {
            // Fallback: use the original text
      parsed.title = 'Mission from AI Agent';
      parsed.description = missionDetails;
    }

    return parsed;
  };

  const handleMissionFormSubmit = async (missionData, missionId = null) => {
    try {
      // If we have a matched historical content ID, use it
      const matchedHistoricalContentId = localStorage.getItem('matchedHistoricalContentId');
      
      if (matchedHistoricalContentId && !missionData.contentId) {
        missionData.contentId = matchedHistoricalContentId;
      } else if (matchedHistoricalContentId && missionData.contentId) {
        // Continue
      } else if (!matchedHistoricalContentId) {
        // Continue
      }
      
      let res;
      if (missionId) {
        // Update existing mission
        res = await axios.put(createApiUrl(`/api/missions/${missionId}`), missionData, { withCredentials: true });
      } else {
        // Create new mission
        
        res = await axios.post(createApiUrl('/missions/create'), missionData, { withCredentials: true });
      }
      
      if (res.data && res.data.success) {
        // Clear the matched historical content ID after successful mission creation
        if (matchedHistoricalContentId) {
          localStorage.removeItem('matchedHistoricalContentId');
        }
        
        // Fetch updated missions list
        const missionsRes = await axios.get(createApiUrl('/missions'), { withCredentials: true });
        if (missionsRes.data && missionsRes.data.success) {
          setMissions(missionsRes.data.data);
        }
        setShowMissionForm(false);
        setEditingMission(null);
      } else {
        // Error handling removed for production
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // Authentication error - session expired
        showToast('Your session has expired. Please log in again.', 'error');
        // Optionally redirect to login or refresh the page
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        showToast(`Error ${missionId ? 'updating' : 'creating'} mission. Please try again.`, 'error');
        console.error(err);
      }
    }
  };

  const handleEditMission = (mission) => {
    setEditingMission(mission);
    setShowMissionForm(true);
  };

  const handleDeleteMission = (mission) => {
    setMissionToDelete(mission);
    setShowDeleteConfirmModal(true);
  };

  const handleSaveMissionToLibrary = (mission) => {
    setContentToSave({ type: 'mission', data: mission });
    setLibraryFormData({
      tags: mission.tags ? mission.tags.join(', ') : '',
      category: 'academic',
      isPublic: false,
      isShared: false
    });
    setShowSaveToLibraryModal(true);
  };

  const handleLibraryFormChange = (field, value) => {
    setLibraryFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmSaveToLibrary = async () => {
    if (!contentToSave) return;

    try {
      const tagsArray = libraryFormData.tags 
        ? libraryFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      const endpoint = contentToSave.type === 'mission' 
        ? '/api/library/save-mission' 
        : '/api/library/save-quiz';
      
      const payload = contentToSave.type === 'mission'
        ? { missionId: contentToSave.data._id, tags: tagsArray, category: libraryFormData.category, isPublic: libraryFormData.isPublic, isShared: libraryFormData.isShared }
        : { quizId: contentToSave.data._id, tags: tagsArray, category: libraryFormData.category, isPublic: libraryFormData.isPublic, isShared: libraryFormData.isShared };

      const response = await axios.post(createApiUrl(endpoint), payload, {
        withCredentials: true
      });

      if (response.data.success) {
        showToast(`${contentToSave.type === 'mission' ? 'Mission' : 'Quiz'} saved to library successfully!`, 'success');
        setShowSaveToLibraryModal(false);
        setContentToSave(null);
      } else {
        showToast(`Failed to save ${contentToSave.type} to library`, 'error');
      }
    } catch (error) {
      console.error('Error saving to library:', error);
      showToast(`Error saving ${contentToSave.type} to library`, 'error');
    }
  };

  const cancelSaveToLibrary = () => {
    setShowSaveToLibraryModal(false);
    setContentToSave(null);
    setLibraryFormData({
      tags: '',
      category: 'academic',
      isPublic: false,
      isShared: false
    });
  };

  const confirmDeleteMission = async () => {
    if (!missionToDelete) return;
    
    try {
      const res = await axios.delete(createApiUrl(`/api/missions/${missionToDelete._id}`), { withCredentials: true });
      if (res.data && res.data.success) {
        // Fetch updated missions list
        const missionsRes = await axios.get(createApiUrl('/missions'), { withCredentials: true });
        if (missionsRes.data && missionsRes.data.success) {
          setMissions(missionsRes.data.data);
        }
        showToast('Mission deleted successfully!', 'success');
      } else {
        showToast('Failed to delete mission.', 'error');
      }
    } catch (err) {
      // Handle specific error cases
      if (err.response?.status === 400) {
        const errorMessage = err.response.data?.message || 'Cannot delete this mission';
        showToast(errorMessage, 'error');
      } else {
        showToast('Error deleting mission.', 'error');
      }
      console.error(err);
    } finally {
      setShowDeleteConfirmModal(false);
      setMissionToDelete(null);
    }
  };

  const _handleCancelEdit = () => {
    setShowMissionForm(false);
    setEditingMission(null);
  };

  // Mission Assignment Functions
  const handleAssignMission = (mission) => {
    setSelectedMission(mission);
    setSelectedStudentsForMissionAssignment([]);
    setAssignmentDueDate('');
    setShowMissionAssignModal(true);
  };

  const handleStudentSelectionForMissionAssignment = (student) => {
    setSelectedStudentsForMissionAssignment(prev => {
      const isSelected = prev.find(s => s._id === student._id);
      if (isSelected) {
        return prev.filter(s => s._id !== student._id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleAssignMissionToStudents = async () => {
    if (selectedStudentsForMissionAssignment.length === 0) {
      showToast('Please select at least one student', 'error');
      return;
    }

    setAssigningMission(true);
    try {
      const requestData = {
        studentIds: selectedStudentsForMissionAssignment.map(s => s._id),
        dueDate: assignmentDueDate || null
      };

      const response = await axios.post(
        `/api/missions/${selectedMission._id}/assign`,
        requestData,
        { withCredentials: true }
      );

      if (response.data.success) {
        showToast('Mission assigned successfully!', 'success');
        setShowMissionAssignModal(false);
        setSelectedMission(null);
        setSelectedStudentsForMissionAssignment([]);
        setAssignmentDueDate('');
        
        // Update the local missions state with the updated mission
        if (response.data.data) {
          setMissions(prevMissions =>
            prevMissions.map(mission =>
              mission._id === selectedMission._id
                ? { ...mission, status: 'active', assignedStudents: response.data.data.assignedStudents }
                : mission
            )
          );
        } else {
          // Fallback to refreshing all data
          fetchData();
        }
      } else {
        console.error('🔍 Assignment failed:', response.data);
        showToast(response.data.message || 'Failed to assign mission', 'error');
      }
    } catch (error) {
                        showToast(`Error assigning mission: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setAssigningMission(false);
    }
  };

  const handleCancelMissionAssignment = () => {
    setShowMissionAssignModal(false);
    setSelectedMission(null);
    setSelectedStudentsForMissionAssignment([]);
    setAssignmentDueDate('');
  };


  const handleViewProgress = (mission) => {
    setMonitoringMission(mission);
    setShowProgressModal(true);
  };

  // Handler functions for assigned missions tab
  const handleDeleteMissionAssignment = (assignmentId, missionName, studentName) => {
    setAssignmentToDelete({ 
      assignmentId, 
      missionName, 
      studentName,
      isMissionAssignment: true
    });
    setShowDeleteModal(true);
  };

  // Handler functions for assigned quizzes tab
  const handleDeleteQuizAssignment = (assignmentId, quizName, studentName) => {
    setQuizAssignmentToDelete({ assignmentId, quizName, studentName });
    setShowQuizAssignmentDeleteModal(true);
  };

  const confirmDeleteQuizAssignment = async () => {
    if (!quizAssignmentToDelete) return;
    
    try {
      const response = await axios.delete(createApiUrl(`/api/quiz-assignments/${quizAssignmentToDelete.assignmentId}`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        showToast('Quiz assignment deleted successfully!', 'success');
        fetchAssignedQuizzes(); // Refresh the list
      } else {
        showToast('Failed to delete quiz assignment', 'error');
      }
    } catch (error) {
      console.error('Error deleting quiz assignment:', error);
      showToast('Error deleting quiz assignment', 'error');
    } finally {
      setShowQuizAssignmentDeleteModal(false);
      setQuizAssignmentToDelete(null);
    }
  };

  const cancelDeleteQuizAssignment = () => {
    setShowQuizAssignmentDeleteModal(false);
    setQuizAssignmentToDelete(null);
  };

  const handleDeleteAllAssignmentsForQuiz = (quizId, quizName) => {
    const quizAssignments = assignedQuizzes.filter(a => a.quizId?._id === quizId);
    setQuizAssignmentToDeleteAll({ quizId, quizName, count: quizAssignments.length });
    setShowQuizAssignmentDeleteAllModal(true);
  };

  const confirmDeleteAllQuizAssignments = async () => {
    if (!quizAssignmentToDeleteAll) return;
    
    try {
      const quizAssignments = assignedQuizzes.filter(a => a.quizId?._id === quizAssignmentToDeleteAll.quizId);
      
      // Delete all assignments for this quiz
      const deletePromises = quizAssignments.map(assignment => 
        axios.delete(createApiUrl(`/api/quiz-assignments/${assignment._id}`), {
          withCredentials: true
        })
      );
      
      await Promise.all(deletePromises);
      
      showToast(`All assignments for "${quizAssignmentToDeleteAll.quizName}" deleted successfully!`, 'success');
      fetchAssignedQuizzes(); // Refresh the list
    } catch (error) {
      console.error('Error deleting all quiz assignments:', error);
      showToast('Error deleting quiz assignments', 'error');
    } finally {
      setShowQuizAssignmentDeleteAllModal(false);
      setQuizAssignmentToDeleteAll(null);
    }
  };

  const cancelDeleteAllQuizAssignments = () => {
    setShowQuizAssignmentDeleteAllModal(false);
    setQuizAssignmentToDeleteAll(null);
  };

  const handleGradeQuiz = (quizGroup) => {
    setGradingActivityType('quiz');
    setGradingActivityId(quizGroup.quizId);
    setGradingActivityName(quizGroup.quizName);
    
    // Check if assignments exists and is an array before mapping
    const assignments = quizGroup.assignments || [];
    setGradingStudents(assignments.map(a => ({
      _id: a.studentId._id,
      contactName: a.studentId.contactName,
      firstName: a.studentId.firstName,
      lastName: a.studentId.lastName,
      yearGrade: a.studentId.yearGrade,
      assignmentId: a._id
    })));
    setShowGradingModal(true);
  };

  const handleDeleteAllAssignmentsForMission = (missionId, missionName) => {
    const missionAssignments = assignedMissions.filter(a => a.missionId?._id === missionId);
    const studentNames = missionAssignments.map(a => 
      a.studentId?.firstName && a.studentId?.lastName 
        ? `${a.studentId.firstName} ${a.studentId.lastName}`
        : a.studentId?.contactName || ''
    ).join(', ');
    
    setAssignmentToDelete({ 
      assignmentId: 'all', 
      missionName, 
      studentName: `${missionAssignments.length} students (${studentNames})`,
      isAllForMission: true,
      missionId
    });
    setShowDeleteModal(true);
  };

  const _handleViewMissionProgress = (missionId) => {
    const mission = missions.find(m => m._id === missionId);
    if (mission) {
      setMonitoringMission(mission);
      setShowProgressModal(true);
    }
  };

  const handleViewStudentProgress = (student) => {
    setMonitoringStudent(student);
    setShowStudentProgressModal(true);
  };

  const handleCloseProgress = () => {
    setShowProgressModal(false);
    setMonitoringMission(null);
  };

  const handleCloseStudentProgress = () => {
    setShowStudentProgressModal(false);
    setMonitoringStudent(null);
  };

  const handleViewMission = (mission) => {
    setViewingMission(mission);
    setShowMissionViewerModal(true);
  };

  const handleCloseMissionViewer = () => {
    setShowMissionViewerModal(false);
    setViewingMission(null);
  };

  const _handleActivateMission = async (mission) => {
    try {
      
      const response = await axios.put(createApiUrl(`/api/missions/${mission._id}/activate`));
      
      if (response.data.success) {
        showToast('Mission activated successfully!', 'success');
        
        // Update the local missions state
        setMissions(prevMissions =>
          prevMissions.map(m =>
            m._id === mission._id
              ? { ...m, status: 'active' }
              : m
          )
        );
      }
    } catch (error) {
            showToast('Failed to activate mission. Please try again.', 'error');
    }
  };

  // Force refresh mission data
  const _refreshMissionData = async () => {
    try {
      const response = await axios.get('/api/missions');
      if (response.data.success) {
        setMissions(response.data.data);
      }
    } catch (error) {
          }
  };

  // Manually update mission status to active
  const updateMissionToActive = async (missionId) => {
    try {
      const response = await axios.put(`/api/missions/${missionId}/status`, { status: 'active' });
      
      if (response.data.success) {
        showToast('Mission status updated to Active!', 'success');
        
        // Update the local missions state
        setMissions(prevMissions =>
          prevMissions.map(m =>
            m._id === missionId
              ? { ...m, status: 'active' }
              : m
          )
        );
      }
    } catch (error) {
            showToast('Failed to update mission status', 'error');
    }
  };

  // Student Management Functions
  // Generate 8-character alphanumeric password
  const _generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setUploadingStudents(true);

    try {
      const response = await axios.post(createApiUrl('/register/student/teacher'), {
        ...newStudent,
        teacherCode: user?.teacherCode || user?.tchCode, // Add teacher code
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        const student = response.data.data.student;
        showToast(`Student added successfully! Username: ${student.username}, Password: ${student.password}`, 'success');
        setShowAddStudentModal(false);
        setNewStudent({
          contactName: '',
          email: '',
          yearGrade: '',
          parentEmail: '',
          parentPhone: '',
          dateOfBirth: ''
        });
        // Refresh students list
        fetchData();
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error adding student', 'error');
    } finally {
      setUploadingStudents(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (bulkStudents.length === 0) {
      showToast('No students to upload. Please upload a CSV file first.', 'error');
      return;
    }
    
    setUploadingStudents(true);

    try {
      // Send students without usernames/passwords - backend will generate them
      const studentsForUpload = bulkStudents.map(student => ({
        contactName: student.contactName,
        email: student.email,
        yearGrade: getStudentGrade(student),
        parentEmail: student.parentEmail || '',
        parentPhone: student.parentPhone || ''
      }));

      const response = await axios.post(createApiUrl('/register/students/bulk'), {
        students: studentsForUpload,
        teacherCode: user?.teacherCode || user?.tchCode
      }, {
        withCredentials: true
      });

      const successfulStudents = response.data.data?.students || [];
      const failed = response.data.data?.errors || [];
      const successfulCount = successfulStudents.length;

      if (successfulCount > 0) {
        const csvData = [
          ['Name', 'Email', 'Grade', 'Username', 'Password', 'Student Code']
        ];

        successfulStudents.forEach(student => {
          csvData.push([
            student.contactName,
            student.email,
            student.yearGrade || student.grade,
            student.username,
            student.password,
            student.studentCode
          ]);
        });

        setGeneratedCSVData(csvData);
      }

      if (successfulCount === 0) {
        const reason = failed[0]?.error || response.data.message || 'No students were created';
        const extra = failed.length > 1 ? ` (${failed.length} failed)` : '';
        showToast(`Upload failed: ${reason}${extra}`, 'error');
        return;
      }

      if (failed.length > 0) {
        showToast(
          `Added ${successfulCount} student(s). ${failed.length} could not be added (${failed[0].error}).`,
          'warning'
        );
      } else {
        showToast(
          `Successfully added ${successfulCount} students! Go to the Students tab to download their login credentials.`,
          'success'
        );
      }

      setShowBulkUploadModal(false);
      setBulkStudents([]);
      setSelectedCSVFile(null);
      fetchData();
    } catch (error) {
      console.error('Bulk upload error:', error);
      const data = error.response?.data;
      const firstError = data?.data?.errors?.[0]?.error;
      const errorMessage = firstError
        ? `${data.message || 'Upload failed'}: ${firstError}`
        : data?.message ||
          data?.details ||
          data?.error ||
          error.message ||
          'Error uploading students';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setUploadingStudents(false);
    }
  };

  const _handleBulkFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split('\n');
          const _headers = lines[0].split(',');
          const students = [];

          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',');
              // Combine First Name and Last Name into Full Name
              const firstName = values[0]?.trim() || '';
              const lastName = values[1]?.trim() || '';
              const fullName = `${firstName} ${lastName}`.trim();
              
              const student = {
                contactName: fullName,
                email: values[2]?.trim() || '',
                grade: values[3]?.trim() || '',
                parentEmail: values[4]?.trim() || '',
                parentPhone: values[5]?.trim() || ''
              };
              if (student.contactName) {
                students.push(student);
              }
            }
          }

          setBulkStudents(students);
        } catch (error) {
          // Error handling removed for production
        }
      };
      reader.readAsText(file);
    }
  };

  const _addBulkStudent = () => {
    setBulkStudents([...bulkStudents, {
      contactName: '',
      email: '',
      yearGrade: '',
      parentEmail: '',
      parentPhone: ''
    }]);
  };

  const _removeBulkStudent = (index) => {
    setBulkStudents(bulkStudents.filter((_, i) => i !== index));
  };

  const _updateBulkStudent = (index, field, value) => {
    const updated = [...bulkStudents];
    updated[index][field] = value;
    setBulkStudents(updated);
  };

  // Messaging functions
  const handleMessageStudent = (student) => {
    setSelectedStudent(student);
    setMessageType('single');
    setSelectedStudents([]);
    setSelectedYearGroupForMessage('all');
    setMessageFromStudentCard(true);
    setShowMessageModal(true);
    setMessageText('');
  };

  const handleOpenMessageModal = () => {
    setSelectedStudent(null);
    setMessageType('single');
    setSelectedStudents([]);
    setSelectedYearGroupForMessage('all');
    setMessageFromStudentCard(false);
    setShowMessageModal(true);
    setMessageText('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    // Validate based on message type
    if (messageType === 'single' && !selectedStudent) {
      // Error handling removed for production
      return;
    }
    if (messageType === 'group' && selectedStudents.length === 0) {
      // Error handling removed for production
      return;
    }

    setSendingMessage(true);
    try {
      let requestData;
      
      if (messageType === 'single') {
        requestData = {
          studentId: selectedStudent._id,
          message: messageText.trim()
        };
      } else if (messageType === 'group') {
        requestData = {
          studentIds: selectedStudents.map(s => s._id),
          message: messageText.trim()
        };
      } else if (messageType === 'all') {
        requestData = {
          message: messageText.trim(),
          sendToAll: true
        };
      }

      const response = await axios.post('/api/teacher/send-message', requestData, {
        withCredentials: true
      });

                 if (response.data.success) {
             const recipientCount = messageType === 'single' ? 1 : 
                                   messageType === 'group' ? selectedStudents.length : 
                                   (Array.isArray(students) ? students.length : 0);
             showToast(`Message sent successfully to ${recipientCount} student(s)!`);
             setShowMessageModal(false);
             setSelectedStudent(null);
             setSelectedStudents([]);
             setMessageText('');
             setMessageType('single');
             setMessageFromStudentCard(false);
           } else {
             showToast(response.data.message || 'Failed to send message', 'error');
           }
             } catch (error) {
                                 showToast('Failed to send message. Please try again.', 'error');
         } finally {
      setSendingMessage(false);
    }
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedStudent(null);
    setSelectedStudents([]);
    setMessageText('');
    setMessageType('single');
    setMessageFromStudentCard(false);
  };

  const handleViewSentMessage = async (message) => {
    // Mark message as viewed by adding a viewed property
    const updatedMessage = { ...message, viewed: true };
    setSelectedSentMessage(updatedMessage);
    setShowSentMessageModal(true);
    
    // Update the message in sentMessages array to mark it as viewed
    setSentMessages(prevMessages => 
      prevMessages.map(msg => 
        msg._id === message._id ? { ...msg, viewed: true } : msg
      )
    );

    // Call API to persist the viewed status in database
    try {
      await axios.post('/api/teacher/mark-message-viewed', {
        messageId: message._id
      }, {
        withCredentials: true
      });
    } catch (error) {
            // Don't show error to user as the UI is already updated
    }
  };

  const closeSentMessageModal = () => {
    setShowSentMessageModal(false);
    setSelectedSentMessage(null);
  };

  const handleStudentSelection = (student) => {
    if (messageType === 'single') {
      setSelectedStudent(student);
      return;
    }
    if (messageType === 'group') {
      setSelectedStudents(prev => {
        const isSelected = prev.find(s => s._id === student._id);
        if (isSelected) {
          return prev.filter(s => s._id !== student._id);
        } else {
          return [...prev, student];
        }
      });
    }
  };

  const getMessageRecipients = () => {
    if (messageType === 'single') {
      return selectedStudent ? [selectedStudent] : [];
    } else if (messageType === 'group') {
      return selectedStudents;
    } else if (messageType === 'all') {
      return Array.isArray(students) ? students : [];
    }
    return [];
  };

  // Get filtered students based on selected year group
  const getFilteredStudents = () => {
    const list = Array.isArray(students) ? students : [];
    if (selectedYearGroup === 'all') {
      return list;
    }
    return list.filter(student => getStudentGrade(student) === selectedYearGroup);
  };

  const getStudentsForMessage = () => {
    const list = Array.isArray(students) ? students : [];
    if (selectedYearGroupForMessage === 'all') {
      return list;
    }
    return list.filter(student => getStudentGrade(student) === selectedYearGroupForMessage);
  };

  // Get paginated students
  const getPaginatedStudents = () => {
    const filteredStudents = getFilteredStudents();
    const startIndex = (currentPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    const filteredStudents = getFilteredStudents();
    return Math.ceil(filteredStudents.length / studentsPerPage);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle students per page change
  const handleStudentsPerPageChange = (newPerPage) => {
    setStudentsPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination when year group filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYearGroup]);

  // Fetch assigned lessons, missions, and quizzes on component mount
  useEffect(() => {
    if (user && (user.role === 'teacher' || user.role === 'schooladmin')) {
      fetchAssignedLessons();
      fetchAssignedMissions();
      fetchAssignedQuizzes();
    }
  }, [user]);

  // Get display name for year group
  const getYearGroupDisplayName = (grade) => {
    if (!grade) return '';
    if (grade === 'K') return 'Reception/Kindergarten';
    if (['1', '2', '3', '4', '5', '6'].includes(grade)) return `Primary/Year ${grade}`;
    return `Year/Grade ${grade}`;
  };

  // Helper function to show toast
  const showToast = (message, type = 'success', duration = 3000) => {
    setToast({ message, type, duration });
  };

  // Helper function to show notification modal
  const showNotification = (title, message, type = 'info') => {
    setNotificationModal({
      show: true,
      title,
      message,
      type
    });
  };

  const hideNotification = () => {
    setNotificationModal({
      show: false,
      title: '',
      message: '',
      type: 'info'
    });
  };

  // Handle tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // If loading, show loading spinner
  if (loading) {
    return <div>Loading...</div>;
  }

  // If there's an error, show error message
  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Dashboard</h4>
          <p>{error}</p>
          <hr />
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setError(null);
              fetchData();
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Teachers don't need individual subscriptions - they access content through their school
  // Only show subscription page for standalone users (which teachers are not)

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}

      {/* Lesson Plan Modal (AI-generated from scheme of work lesson breakdown link) */}
      <Modal isOpen={showLessonPlanModal} onClose={() => setShowLessonPlanModal(false)} className="lesson-plan-modal">
        <div className="modal-header">
          <h3>Lesson plan: {lessonPlanTitle}</h3>
          <button type="button" className="close-btn" onClick={() => setShowLessonPlanModal(false)} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          {lessonPlanLoading ? (
            <p className="text-muted mb-0">Generating lesson plan…</p>
          ) : lessonPlanGenerated ? (
            <>
              {lessonPlanFromCache && <p className="text-muted small mb-2">Loaded from cache.</p>}
              <div className="lesson-plan-content mb-3" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: '60vh', overflowY: 'auto', padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
                {lessonPlanGenerated}
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-primary" onClick={handlePrintLessonPlan}>Print</button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => {
                    openMissionFormWithPrefill(lessonPlanGenerated, {
                      title: lessonPlanTitle,
                      subject: lessonPlanSubject || curriculumSubject,
                      gradeLevel: lessonPlanYearGroup || curriculumGrade
                    });
                    setShowLessonPlanModal(false);
                  }}
                >
                  Create Mission
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setCurriculumData({
                      content: lessonPlanGenerated,
                      subject: lessonPlanSubject || curriculumSubject,
                      gradeLevel: lessonPlanYearGroup || curriculumGrade
                    });
                    setShowQuizCreationModal(true);
                    setShowLessonPlanModal(false);
                  }}
                >
                  Create Quiz
                </button>
              </div>
            </>
          ) : (
            <p className="text-muted mb-0">No lesson plan was generated. Try again or close.</p>
          )}
        </div>
      </Modal>

      {/* Delete Lesson Plan Confirmation Modal */}
      <Modal isOpen={showDeleteLessonPlanModal} onClose={cancelDeleteLessonPlan} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Delete Lesson Plan</h3>
          <button type="button" className="close-btn" onClick={cancelDeleteLessonPlan} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="mb-3">
            Are you sure you want to delete <strong>{lessonPlanToDelete?.lessonTitle || 'this lesson plan'}</strong>? This cannot be undone.
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-secondary" onClick={cancelDeleteLessonPlan}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={confirmDeleteLessonPlan}>Delete</button>
          </div>
        </div>
      </Modal>

      {/* Clear All Lesson Plans Confirmation Modal */}
      <Modal isOpen={showClearAllLessonPlansModal} onClose={() => setShowClearAllLessonPlansModal(false)} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Clear All Lesson Plans</h3>
          <button type="button" className="close-btn" onClick={() => setShowClearAllLessonPlansModal(false)} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="mb-3">
            Are you sure you want to delete <strong>all</strong> saved lesson plans? This cannot be undone.
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-secondary" onClick={() => setShowClearAllLessonPlansModal(false)}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={confirmClearAllLessonPlans}>Clear all</button>
          </div>
        </div>
      </Modal>

      {/* Clear Curriculum Cache Confirmation Modal */}
      <Modal isOpen={showClearCurriculumCacheModal} onClose={() => setShowClearCurriculumCacheModal(false)} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Clear Curriculum Cache</h3>
          <button type="button" className="close-btn" onClick={() => setShowClearCurriculumCacheModal(false)} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="mb-3">
            Are you sure you want to clear the <strong>curriculum cache</strong>? All cached curriculum match results will be removed. Matching the same document again will be re-processed. This cannot be undone.
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-secondary" onClick={() => setShowClearCurriculumCacheModal(false)}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={confirmClearCurriculumCache}>Clear cache</button>
          </div>
        </div>
      </Modal>

      {/* Delete Single Curriculum Cache Entry Confirmation Modal */}
      <Modal isOpen={showDeleteCurriculumCacheModal} onClose={cancelDeleteCurriculumCache} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Delete Cached Document</h3>
          <button type="button" className="close-btn" onClick={cancelDeleteCurriculumCache} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="mb-3">
            Are you sure you want to delete <strong>{curriculumCacheToDelete?.filename || 'this cached document'}</strong>? Matching the same document again will be re-processed. This cannot be undone.
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-secondary" onClick={cancelDeleteCurriculumCache}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={confirmDeleteCurriculumCache}>Delete</button>
          </div>
        </div>
      </Modal>

      {/* Subscription Expired Modal (Lessons tab) */}
      <Modal isOpen={showSubscriptionExpiredModal} onClose={() => setShowSubscriptionExpiredModal(false)} className="subscription-expired-modal">
        <div className="modal-header">
          <h3>Subscription expired</h3>
          <button type="button" className="close-btn" onClick={() => setShowSubscriptionExpiredModal(false)} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p className="mb-3">Please contact your school admin to renew your subscription.</p>
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-primary" onClick={() => setShowSubscriptionExpiredModal(false)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Lesson Assignment Modal */}
      <Modal isOpen={lessonAssignmentModalOpen} onClose={handleCancelLessonAssignment} className="lesson-assignment-modal">
        <div className="modal-header">
          <h3>Assign Lesson: {selectedLesson?.name}</h3>
          <button className="close-btn" onClick={handleCancelLessonAssignment}>×</button>
        </div>
        <div className="modal-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Filter by Year Group</label>
              <select 
                className={`form-select ${styles.yearGroupSelect}`}
                value={selectedYearGroupForLesson}
                onChange={(e) => setSelectedYearGroupForLesson(e.target.value)}
              >
                <option value="all">All year groups</option>
                {yearGroups.map(grade => (
                  <option key={grade} value={grade}>
                    {getYearGroupDisplayName(grade)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group mb-3">
            <label className="form-label">Select Students</label>
            <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {/* Select All Checkbox */}
              <div className="form-check mb-2" style={{ display: 'inline-block' }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="select-all-students"
                  checked={isAllStudentsSelected()}
                  onChange={handleSelectAllStudents}
                />
                <label className="form-check-label fw-bold" htmlFor="select-all-students">
                  Select All Students in {selectedYearGroupForLesson === 'all' ? 'All year groups' : getYearGroupDisplayName(selectedYearGroupForLesson)}
                </label>
              </div>
              
              {/* Individual Student Checkboxes */}
              {getFilteredStudents()
                .filter(student => {
                  const isTeacherStudent = student.teacherCode === user?.teacherCode;
                  const matchesYearGroup = selectedYearGroupForLesson === 'all' || 
                    (student.yearGrade || student.grade || student.Grade || student.yearGroup || '') === selectedYearGroupForLesson;
                  return isTeacherStudent && matchesYearGroup;
                })
                .map(student => (
                  <div key={student._id} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`lesson-assignment-student-${student._id}`}
                      checked={selectedStudentsForLessonAssignment.find(s => s._id === student._id) !== undefined}
                      onChange={() => handleStudentSelectionForLessonAssignment(student)}
                    />
                    <label className="form-check-label" htmlFor={`lesson-assignment-student-${student._id}`}>
                      {student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.contactName}{getStudentGrade(student) && ` (${getStudentGrade(student)})`}
                    </label>
                  </div>
                ))}
            </div>
            {selectedStudentsForLessonAssignment.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">
                  Selected: {selectedStudentsForLessonAssignment.length} student(s)
                </small>
              </div>
            )}
          </div>
          
          <div className="form-group mb-3">
            <label className="form-label">Instructions/Notes (Optional)</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Add any specific instructions or notes for this lesson assignment..."
              value={lessonAssignmentNotes}
              onChange={(e) => setLessonAssignmentNotes(e.target.value)}
            />
            <small className="form-text text-muted">
              These notes will be visible to students when they view the assigned lesson.
            </small>
          </div>
          
          <div className="d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleCancelLessonAssignment}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleAssignLessonToStudents}
              disabled={selectedStudentsForLessonAssignment.length === 0}
            >
              Assign Lesson
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={cancelDeleteAssignment} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Confirm Deletion</h3>
          <button className="close-btn" onClick={cancelDeleteAssignment}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>
            Are you sure you want to delete the assignment of 
            <strong> "{assignmentToDelete?.lessonName}"</strong> to 
            <strong> {assignmentToDelete?.studentName}</strong>?
          </p>
          <p className="text-muted">
            {assignmentToDelete?.isAllForLesson 
              ? 'All students will no longer have access to this lesson assignment.'
              : 'The student will no longer have access to this lesson assignment.'
            }
          </p>
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={cancelDeleteAssignment}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger"
            onClick={confirmDeleteAssignment}
          >
            <i className="fas fa-trash me-2"></i>
            Delete Assignment
          </button>
        </div>
      </Modal>

      {/* Quiz Assignment Delete Confirmation Modal */}
      <Modal isOpen={showQuizAssignmentDeleteModal} onClose={cancelDeleteQuizAssignment} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Confirm Quiz Assignment Deletion</h3>
          <button className="close-btn" onClick={cancelDeleteQuizAssignment}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>
            Are you sure you want to delete this quiz assignment?
          </p>
          <div className="assignment-details">
            <strong>Quiz:</strong> {quizAssignmentToDelete?.quizName || 'Unknown'}
            <br />
            <strong>Student:</strong> {quizAssignmentToDelete?.studentName || 'Unknown'}
          </div>
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={cancelDeleteQuizAssignment}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={confirmDeleteQuizAssignment}
          >
            <i className="fas fa-trash me-2"></i>
            Delete Assignment
          </button>
        </div>
      </Modal>

      {/* Quiz Assignment Delete All Confirmation Modal */}
      <Modal isOpen={showQuizAssignmentDeleteAllModal} onClose={cancelDeleteAllQuizAssignments} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Confirm Delete All Quiz Assignments</h3>
          <button className="close-btn" onClick={cancelDeleteAllQuizAssignments}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>
            Are you sure you want to delete ALL assignments for this quiz?
          </p>
          <div className="assignment-details">
            <strong>Quiz:</strong> {quizAssignmentToDeleteAll?.quizName || 'Unknown'}
            <br />
            <strong>Assignments to Delete:</strong> {quizAssignmentToDeleteAll?.count || 0} assignment(s)
          </div>
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={cancelDeleteAllQuizAssignments}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={confirmDeleteAllQuizAssignments}
          >
            <i className="fas fa-trash me-2"></i>
            Delete All Assignments
          </button>
        </div>
      </Modal>

      {/* Grading Modal */}
      <GradingModal
        isOpen={showGradingModal}
        onClose={closeGradingModal}
        activityType={gradingActivityType}
        activityId={gradingActivityId}
        activityName={gradingActivityName}
        students={gradingStudents}
        onGradeSubmitted={handleGradeSubmitted}
      />

      {/* Save to Library Modal */}
      <Modal isOpen={showSaveToLibraryModal} onClose={cancelSaveToLibrary}>
        <div className="modal-header">
          <h3>Save {contentToSave?.type === 'mission' ? 'Mission' : 'Quiz'} to Library</h3>
          <button className="close-btn" onClick={cancelSaveToLibrary}>×</button>
        </div>
        <div className="modal-body">
          {contentToSave && (
            <div>
              <div className="mb-3">
                <h5>{contentToSave.data.title}</h5>
                <p className="text-muted">{contentToSave.data.description}</p>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Tags (comma-separated)</label>
                <input
                  type="text"
                  className="form-control"
                  value={libraryFormData.tags}
                  onChange={(e) => handleLibraryFormChange('tags', e.target.value)}
                  placeholder="e.g., math, algebra, equations"
                />
                <small className="form-text text-muted">
                  Add tags to help you find this {contentToSave.type} later
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={libraryFormData.category}
                  onChange={(e) => handleLibraryFormChange('category', e.target.value)}
                >
                  <option value="academic">Academic</option>
                  <option value="assessment">Assessment</option>
                  <option value="practice">Practice</option>
                  <option value="review">Review</option>
                  <option value="homework">Homework</option>
                </select>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isPublic"
                    checked={libraryFormData.isPublic}
                    onChange={(e) => handleLibraryFormChange('isPublic', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isPublic">
                    Make this {contentToSave.type} public (visible to other teachers)
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isShared"
                    checked={libraryFormData.isShared}
                    onChange={(e) => handleLibraryFormChange('isShared', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isShared">
                    Share with school colleagues
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cancelSaveToLibrary}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmSaveToLibrary}
          >
            <i className="fas fa-bookmark me-2"></i>
            Save to Library
          </button>
        </div>
      </Modal>

    <BaseDashboard
      title="Teacher Dashboard"
      tabs={tabs}
      user={user}
      loading={loading}
      defaultActiveTab={activeTab}
      onTabChange={handleTabChange}
      tabBadges={{
        messages: unreadMessageCount
      }}
      banner={<ImpersonationBanner user={user} />}
    >
      {/* Overview Tab */}
      <div tabId="overview" className="overview-tab-content">
        {/* Switch to School Admin Button - Only show for school admins currently in teacher role */}
        {user?.isSchAdmin && user?.currentActiveRole === 'teacher' && (
          <div className="d-flex justify-content-end mb-3">
            <button 
              className="btn btn-outline-primary"
              onClick={handleSwitchToSchoolAdmin}
              style={{
                backgroundColor: 'var(--lightOrange)',
                borderColor: 'var(--darkPurple)',
                color: 'var(--darkPurple)',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--darkPurple)';
                e.target.style.color = 'var(--lightOrange)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--lightOrange)';
                e.target.style.color = 'var(--darkPurple)';
              }}
            >
              <i className="fas fa-user-shield me-2"></i>
              Switch to School Admin Dashboard
            </button>
          </div>
        )}
        
        <div className={`${styles.overviewRow}`}>
          <div className={`${styles.overviewCard}`}>
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Available Lessons</h5>
                <div className="card-content flex-grow-1">
                  <p>
                    <strong>Count:</strong> {getSubscriptionLessons(assets).length}<br/>
                    <strong>From Subscriptions:</strong> {getSubscriptionLessons(assets).length}<br/>
                    <strong>Curriculum Topics:</strong> {assets.filter(asset => asset.type === 'curriculum').length}
                  </p>
                </div>
                <button className="btn btn-primary mt-auto" onClick={() => handleTabChange('lessons')}>Browse Lessons</button>
              </div>
            </div>
          </div>
          <div className={`${styles.overviewCard}`}>
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Assigned Missions</h5>
                <div className="card-content flex-grow-1">
                  <p>
                    <strong>Total Assignments:</strong> {assignedMissions.length}<br/>
                    <strong>Completed:</strong> {assignedMissions.filter(mission => mission.status === 'completed').length}<br/>
                    <strong>In Progress:</strong> {assignedMissions.filter(mission => mission.status === 'in_progress').length}<br/>
                    <strong>Assigned:</strong> {assignedMissions.filter(mission => mission.status === 'assigned').length}
                  </p>
                </div>
                <button className="btn btn-primary mt-auto" onClick={() => handleTabChange('assignmissions')}>View Assignments</button>
              </div>
            </div>
          </div>
          <div className={`${styles.overviewCard}`}>
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Assigned Lessons</h5>
                <div className="card-content flex-grow-1">
                  <p>
                    <strong>Total Assignments:</strong> {assignedLessons.length}<br/>
                    <strong>Completed:</strong> {assignedLessons.filter(lesson => lesson.status === 'completed' || lesson.status === 'graded').length}<br/>
                    <strong>In Progress:</strong> {assignedLessons.filter(lesson => lesson.status === 'in-progress').length}<br/>
                    <strong>Assigned:</strong> {assignedLessons.filter(lesson => lesson.status === 'assigned').length}
                  </p>
                </div>
                <button className="btn btn-primary mt-auto" onClick={() => handleTabChange('assignedlessons')}>View Assignments</button>
              </div>
            </div>
          </div>
          <div className={`${styles.overviewCard}`}>
            <div className="card h-100">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title">Assigned Quizzes</h5>
                <div className="card-content flex-grow-1">
                  <p>
                    <strong>Total Assignments:</strong> {assignedQuizzes.length}<br/>
                    <strong>Completed:</strong> {assignedQuizzes.filter(quiz => quiz.status === 'completed' || quiz.status === 'graded').length}<br/>
                    <strong>In Progress:</strong> {assignedQuizzes.filter(quiz => quiz.status === 'in-progress').length}<br/>
                    <strong>Assigned:</strong> {assignedQuizzes.filter(quiz => quiz.status === 'assigned').length}
                  </p>
                </div>
                <button className="btn btn-primary mt-auto" onClick={() => handleTabChange('assignedquizzes')}>View Assignments</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lessons Tab */}
      <div tabId="lessons" className={`lessons-tab-content ${styles.lessonsTabContent}`}>

        {getSubscriptionLessons(assets).length === 0 ? (
          <div className="text-center">
            <p>No lessons found. Your lessons will appear here once you have active subscriptions.</p>
            <button className="btn btn-primary">Browse Available Plans</button>
          </div>
        ) : (
          <div className={`lessons-grid ${styles.lessonsGrid}`}>
            {getSubscriptionLessons(assets).map((asset) => {
              // Check if subscription is expired (from backend flag or subscription status)
              const hasExpiredSubscription = asset.isInactive || asset.subscriptionStatus === 'expired';
              const lessonNumber = asset.name ? parseInt(asset.name.match(/\d+/)?.[0] || '0') : 0;
              const isLesson1 = lessonNumber === 1;
              // Disable if subscription expired AND it's not lesson 1
              const shouldDisable = hasExpiredSubscription && !isLesson1;
              const videoPlayback = getAssetVideoPlayback(asset);
              const lessonDocuments = getAssetDocuments(asset);
              const hasVideo = Boolean(videoPlayback?.thumbnail || videoPlayback?.url);
              
              return (
              <div 
                key={asset._id} 
                className={`content-card lesson-card ${shouldDisable ? 'lesson-inactive' : ''}`}
                style={shouldDisable ? { opacity: 0.6, pointerEvents: 'none' } : {}}
              >
                {shouldDisable && (
                  <div className="lesson-expired-badge">
                    Subscription Expired
                  </div>
                )}

                <h5 className="card-title">{asset.name || 'Untitled Lesson'}</h5>
                <p className="card-content lesson-card-description preserve-whitespace">
                  {asset.description || 'No description available'}
                </p>

                <div className="lesson-card-body">
                  <div
                    className={`video-container video-thumbnail lesson-card-video-wrap ${
                      hasVideo && !shouldDisable ? 'clickable' : ''
                    }`}
                    onClick={
                      hasVideo && !shouldDisable ? () => handleVideoClick(asset) : undefined
                    }
                    title={
                      shouldDisable
                        ? 'Subscription expired - Lesson unavailable'
                        : hasVideo
                          ? 'Click to play video'
                          : undefined
                    }
                    style={shouldDisable ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                  >
                    {videoPlayback?.thumbnail && (
                      <img
                        src={videoPlayback.thumbnail}
                        alt={`Thumbnail for ${asset.name}`}
                        className="thumbnail-image"
                      />
                    )}
                    {videoPlayback?.url && !videoPlayback.thumbnail && (
                      <video className="lesson-card-video" muted loop playsInline>
                        <source src={videoPlayback.url} type="video/mp4" />
                      </video>
                    )}
                    {!hasVideo && (
                      <div className="lesson-card-video-placeholder">No video uploaded</div>
                    )}
                    {hasVideo && (
                      <div className="play-overlay">
                        <span className="play-icon">▶</span>
                      </div>
                    )}
                  </div>

                  {lessonDocuments.length > 0 && (
                    <div className="teacher-guides">
                      <h6>Teacher Guides:</h6>
                      <div className="guide-links">
                        {lessonDocuments.map((doc, docIndex) => (
                          <a
                            key={docIndex}
                            href={doc.url || doc.documentUrl}
                            className="guide-link"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!shouldDisable) handleDocumentClick(doc);
                            }}
                          >
                            <i className="fas fa-file-pdf" />{' '}
                            {assetFileDisplayName(doc) || 'Document'}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="content-card-actions">
                  <button 
                    className="btn btn-outline-success"
                    onClick={() => handleAssignLesson(asset)}
                    disabled={shouldDisable}
                    style={shouldDisable ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    Assign
                  </button>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>

      {/* Assigned Lessons Tab */}
      <div tabId="assignedlessons" className={`assigned-lessons-tab-content ${styles.lessonsTabContent}`}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Assigned Lessons</h4>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={fetchAssignedLessons}
            disabled={loadingAssignedLessons}
          >
            {loadingAssignedLessons ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingAssignedLessons ? (
          <div className="text-center">
            <p>Loading assigned lessons...</p>
          </div>
        ) : assignedLessons.length === 0 ? (
          <div className="text-center">
            <p>No lessons have been assigned yet.</p>
            <p className="text-muted">Go to the Lessons tab to assign lessons to your students.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px',
            padding: '0 15px'
          }}>
            {getGroupedAssignments().map((group) => (
              <div key={group.lessonId}>
                <div className="card h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                      <h5 className="card-title text-primary">
                        {group.lessonName}
                      </h5>
                      {group.lessonDescription && (
                        <p className="card-text text-muted small">
                          {group.lessonDescription.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Students:</strong>
                        <span className="badge bg-info">
                          {group.assignments.length} assigned
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Assigned:</strong>
                        <span className="text-muted">
                          {new Date(group.earliestAssignment).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <strong>Status:</strong>
                        <div className="d-flex gap-1">
                          {group.statusCounts.completed > 0 && (
                            <span className="badge bg-success">{group.statusCounts.completed} completed</span>
                          )}
                          {group.statusCounts.in_progress > 0 && (
                            <span className="badge bg-warning">{group.statusCounts.in_progress} in progress</span>
                          )}
                          {group.statusCounts.assigned > 0 && (
                            <span className="badge bg-secondary">{group.statusCounts.assigned} assigned</span>
                          )}
                          {group.statusCounts.overdue > 0 && (
                            <span className="badge bg-danger">{group.statusCounts.overdue} overdue</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Student List */}
                      <div className="mb-3">
                        <strong>Assigned Students:</strong>
                        <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                          {group.assignments.map((assignment) => (
                            <div key={assignment._id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <span className="small">
                                {assignment.studentId?.firstName && assignment.studentId?.lastName 
                                  ? `${assignment.studentId.firstName} ${assignment.studentId.lastName}`
                                  : assignment.studentId?.contactName || ''
                                }{assignment.studentId?.grade && ` (${assignment.studentId.grade})`}
                              </span>
                              <div className="d-flex gap-1">
                                <span className={`badge badge-sm ${
                                  assignment.status === 'completed' ? 'bg-success' :
                                  assignment.status === 'in_progress' ? 'bg-warning' :
                                  assignment.status === 'overdue' ? 'bg-danger' :
                                  'bg-secondary'
                                }`}>
                                  {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1).replace('_', ' ')}
                                </span>
                                <button 
                                  className="btn btn-sm"
                                  onClick={() => handleDeleteLessonAssignment(assignment._id, assignment.lessonId?.name, assignment.studentId?.contactName)}
                                  title="Delete this student's assignment"
                                  style={{
                                    backgroundColor: 'var(--darkPurple)',
                                    borderColor: 'var(--darkPurple)',
                                    color: '#dc3545',
                                    fontSize: '0.7rem',
                                    padding: '0.3rem 0.4rem',
                                    minWidth: 'auto',
                                    width: 'auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#dc3545';
                                    e.target.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--darkPurple)';
                                    e.target.style.color = '#dc3545';
                                  }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: 'white',
                            marginLeft: '8px'
                          }}
                          onClick={() => handleGradeLesson(group)}
                          title="Grade this lesson"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#28a745';
                            e.target.style.borderColor = '#28a745';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.borderColor = 'var(--darkPurple)';
                          }}
                        >
                          <i className="fas fa-star me-1"></i>
                          Grade
                        </button>
                        <button 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: '#dc3545'
                          }}
                          onClick={() => handleDeleteAllAssignmentsForLesson(group.lessonId, group.lessonName)}
                          title="Delete all assignments for this lesson"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missions Tab */}
      <div tabId="missions" className={`missions-tab-content ${styles.missionsTabContent}`}>

        <MissionModal 
          isOpen={showMissionForm} 
          onClose={() => { setShowMissionForm(false); setEditingMission(null); setShowPreview(false); }}
          title={editingMission ? 'Edit Mission' : 'Create Mission'}
          onPreviewToggle={() => setShowPreview(!showPreview)}
          onReset={() => {
            // Just reset the form, don't close modal or switch tabs
            if (missionFormResetRef.current) {
              missionFormResetRef.current();
            }
          }}
        >
          <MissionForm
            onSubmit={handleMissionFormSubmit}
            mission={editingMission}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            onReset={missionFormResetRef}
            subscriptionLessons={getSelectableSubscriptionLessons(assets)}
          />
        </MissionModal>
        
        {/* Mission Assignment Modal */}
        <Modal isOpen={showMissionAssignModal} onClose={handleCancelMissionAssignment}>
          <div className="modal-header">
            <h3>Assign Mission: {selectedMission?.title}</h3>
            <button className="close-btn" onClick={handleCancelMissionAssignment}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group mb-3">
              <label className="form-label">Select Students</label>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Filter by Year Group</label>
                  <select 
                    className={`form-select ${styles.yearGroupSelect}`}
                    value={selectedYearGroupForMessage}
                    onChange={(e) => setSelectedYearGroupForMessage(e.target.value)}
                  >
                    <option value="all">All year groups</option>
                    {yearGroups.map(grade => (
                      <option key={grade} value={grade}>
                        {getYearGroupDisplayName(grade)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {/* Select All Checkbox */}
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="select-all-mission-students"
                    checked={getFilteredStudents()
                      .filter(student => selectedYearGroupForMessage === 'all' || 
                        (student.yearGrade || '') === selectedYearGroupForMessage)
                      .every(student => 
                        selectedStudentsForMissionAssignment.find(s => s._id === student._id) !== undefined
                      )}
                    onChange={() => {
                      const filteredStudents = getFilteredStudents()
                        .filter(student => selectedYearGroupForMessage === 'all' || 
                          (student.yearGrade || '') === selectedYearGroupForMessage);
                      
                      const allSelected = filteredStudents.every(student => 
                        selectedStudentsForMissionAssignment.find(s => s._id === student._id) !== undefined
                      );
                      
                      if (allSelected) {
                        // Deselect all filtered students
                        const filteredStudentIds = filteredStudents.map(s => s._id);
                        setSelectedStudentsForMissionAssignment(prev => 
                          prev.filter(s => !filteredStudentIds.includes(s._id))
                        );
                      } else {
                        // Select all filtered students
                        const newSelections = filteredStudents.filter(student => 
                          !selectedStudentsForMissionAssignment.find(s => s._id === student._id)
                        );
                        setSelectedStudentsForMissionAssignment(prev => [...prev, ...newSelections]);
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="select-all-mission-students">
                    <strong>Select All Students in {selectedYearGroupForMessage === 'all' ? 'All year groups' : `Year ${selectedYearGroupForMessage}`}</strong>
                  </label>
                </div>
                
                {getFilteredStudents()
                  .filter(student => selectedYearGroupForMessage === 'all' || 
                    (student.yearGrade || '') === selectedYearGroupForMessage)
                  .map(student => (
                    <div key={student._id} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`assignment-student-${student._id}`}
                        checked={selectedStudentsForMissionAssignment.find(s => s._id === student._id) !== undefined}
                        onChange={() => handleStudentSelectionForMissionAssignment(student)}
                      />
                      <label className="form-check-label" htmlFor={`assignment-student-${student._id}`}>
                        {student.contactName}{student.yearGrade && ` (Year ${student.yearGrade})`}
                      </label>
                    </div>
                  ))}
              </div>
              {selectedStudentsForMissionAssignment.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    Selected: {selectedStudentsForMissionAssignment.length} student(s)
                  </small>
                </div>
              )}
            </div>
            
            <div className="form-group mb-3">
              <label className="form-label">Due Date (Optional)</label>
              <input
                type="date"
                className="form-control"
                value={assignmentDueDate}
                onChange={(e) => setAssignmentDueDate(e.target.value)}
                style={{ maxWidth: '200px', height: '32px' }}
              />
            </div>
            
            <div className="d-flex justify-content-end gap-2">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleCancelMissionAssignment}
                disabled={assigningMission}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleAssignMissionToStudents}
                disabled={assigningMission || selectedStudentsForMissionAssignment.length === 0}
              >
                {assigningMission ? 'Assigning...' : 'Assign Mission'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Mission Progress Monitoring Modal */}
        <Modal isOpen={showProgressModal} onClose={handleCloseProgress}>
          <div className="modal-header">
            <h3>Student Progress: {monitoringMission?.title}</h3>
            <button className="close-btn" onClick={handleCloseProgress}>×</button>
          </div>
          <div className="modal-body">
            {monitoringMission && (
              <TeacherMissionMonitoring missionId={monitoringMission._id} />
            )}
          </div>
        </Modal>

        {missions.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>No missions found. Create your first mission to get started!</p>
            <div className={styles.emptyStateButton}>
              <button className={styles.createMissionButton} onClick={() => setShowMissionForm(true)}>
                Create Mission
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.missionsHeader}>
              <h4 className={styles.missionsTitle}>Missions</h4>
              <button className={styles.createMissionButton} onClick={() => setShowMissionForm(true)}>
                Create Mission
              </button>
            </div>
                        <div className={styles.missionGrid}>
              {missions.map((mission) => (
                <div key={mission._id} className={styles.missionGridItem}>
                  <div className={`card h-100 ${styles.missionCard}`}>
                    <div className={`card-body d-flex flex-column ${styles.missionCardBody}`}>
                      <h5 className={`card-title ${styles.missionCardTitle}`}>{mission.title}</h5>
                      <h6 className={`card-subtitle mb-2 ${styles.missionCardSubtitle} ${mission.status === 'active' ? 'light-orange-text' : mission.status === 'draft' ? 'text-warning' : 'text-muted'}`}>
                        Status: {mission.status === 'draft' ? 'Draft' : mission.status === 'active' ? 'Active' : mission.status}
                        {mission.status === 'draft' && (
                          <span className="badge bg-warning ms-2">Not Assigned</span>
                        )}
                        {mission.status === 'active' && (
                          <span className="badge bg-success ms-2">Assigned</span>
                        )}
                      </h6>
                      <div className="card-content flex-grow-1">
                        <div className={`mission-description ${styles.missionDescription}`}>
                          <SimpleMarkdown>{mission.description}</SimpleMarkdown>
                        </div>
                      </div>
                      <div className={`mission-meta mt-2 mb-3 ${styles.missionMeta}`}>
                        <span className="badge bg-primary me-1">{mission.difficulty || 'Medium'}</span>
                        {mission.dueDate && (
                          <span className="badge bg-info me-1">
                            Due: {new Date(mission.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className={`${styles.missionActions}`}>
                        <button 
                          className={`btn btn-primary btn-sm ${styles.missionButton}`}
                          onClick={() => handleViewMission(mission)}
                        >
                          View Mission
                        </button>
                        <button 
                          className={`btn btn-outline-warning btn-sm ${styles.missionButtonProgress}`}
                          onClick={() => handleViewProgress(mission)}
                        >
                          Progress
                        </button>
                        <button 
                          className={`btn btn-outline-info btn-sm ${styles.missionButtonAssign}`}
                          onClick={() => handleAssignMission(mission)}
                        >
                          Assign
                        </button>
                        <button 
                          className={`btn btn-outline-success btn-sm ${styles.missionButtonOutline}`}
                          onClick={() => handleEditMission(mission)}
                        >
                          Edit
                        </button>
                        <button 
                          className={`btn btn-sm ${styles.missionButtonLibrary}`}
                          onClick={() => handleSaveMissionToLibrary(mission)}
                          title="Save to Library"
                        >
                          <i className="fas fa-bookmark"></i>
                        </button>
                        <button 
                          className={`btn btn-outline-danger btn-sm ${styles.missionButtonDanger}`}
                          onClick={() => handleDeleteMission(mission)}
                        >
                          Delete
                        </button>
                        {/* Temporary fix button for assigned missions that show as draft */}
                        {mission.status === 'draft' && mission.assignedStudents && mission.assignedStudents.length > 0 && (
                          <button 
                            className="btn btn-warning btn-sm"
                            onClick={() => updateMissionToActive(mission._id)}
                            style={{ border: '2px solid orange', backgroundColor: 'yellow', color: 'black' }}
                          >
                            Fix Status
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Assigned Missions Tab */}
      <div tabId="assignmissions" className={`assigned-missions-tab-content ${styles.lessonsTabContent}`}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Assigned Missions</h4>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={fetchAssignedMissions}
            disabled={loadingAssignedMissions}
          >
            {loadingAssignedMissions ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingAssignedMissions ? (
          <div className="text-center">
            <p>Loading assigned missions...</p>
          </div>
        ) : assignedMissions.length === 0 ? (
          <div className="text-center">
            <p>No missions have been assigned yet.</p>
            <p className="text-muted">Go to the Missions tab to assign missions to your students.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px',
            padding: '0 15px'
          }}>
            {getGroupedMissionAssignments().map((group) => (
              <div key={group.missionId}>
                <div className="card h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                      <h5 className="card-title text-primary">
                        {group.missionName}
                      </h5>
                      {group.missionDescription && (
                        <p className="card-text text-muted small">
                          {group.missionDescription.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Students:</strong>
                        <span className="badge bg-info">
                          {group.assignments.length} assigned
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Assigned:</strong>
                        <span className="text-muted">
                          {new Date(group.earliestAssignment).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <strong>Status:</strong>
                        <div className="d-flex gap-1">
                          {group.statusCounts.completed > 0 && (
                            <span className="badge bg-success">{group.statusCounts.completed} completed</span>
                          )}
                          {group.statusCounts.in_progress > 0 && (
                            <span className="badge bg-warning">{group.statusCounts.in_progress} in progress</span>
                          )}
                          {group.statusCounts.assigned > 0 && (
                            <span className="badge bg-secondary">{group.statusCounts.assigned} assigned</span>
                          )}
                          {group.statusCounts.overdue > 0 && (
                            <span className="badge bg-danger">{group.statusCounts.overdue} overdue</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Student List */}
                      <div className="mb-3">
                        <strong>Assigned Students:</strong>
                        <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                          {group.assignments.map((assignment) => (
                            <div key={assignment._id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <span className="small">
                                {assignment.studentId?.firstName && assignment.studentId?.lastName 
                                  ? `${assignment.studentId.firstName} ${assignment.studentId.lastName}`
                                  : assignment.studentId?.contactName || ''
                                }{assignment.studentId?.grade && ` (${assignment.studentId.grade})`}
                              </span>
                              <div className="d-flex gap-1">
                                <span className={`badge badge-sm ${
                                  assignment.status === 'completed' ? 'bg-success' :
                                  assignment.status === 'in_progress' ? 'bg-warning' :
                                  assignment.status === 'overdue' ? 'bg-danger' :
                                  'bg-secondary'
                                }`}>
                                  {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1).replace('_', ' ')}
                                </span>
                                {assignment.status === 'completed' && (
                                  <button 
                                    className="btn btn-sm"
                                    onClick={() => handleViewStudentWork(assignment._id)}
                                    title="View student's completed work"
                                    style={{
                                      backgroundColor: 'var(--darkPurple)',
                                      borderColor: 'var(--darkPurple)',
                                      color: '#28a745',
                                      fontSize: '0.7rem',
                                      padding: '0.3rem 0.4rem',
                                      minWidth: 'auto',
                                      width: 'auto',
                                      marginRight: '4px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#28a745';
                                      e.target.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'var(--darkPurple)';
                                      e.target.style.color = '#28a745';
                                    }}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                )}
                                <button 
                                  className="btn btn-sm"
                                  onClick={() => handleDeleteMissionAssignment(assignment._id, assignment.missionId?.title, assignment.studentId?.contactName)}
                                  title="Delete this student's assignment"
                                  style={{
                                    backgroundColor: 'var(--darkPurple)',
                                    borderColor: 'var(--darkPurple)',
                                    color: '#dc3545',
                                    fontSize: '0.7rem',
                                    padding: '0.3rem 0.4rem',
                                    minWidth: 'auto',
                                    width: 'auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#dc3545';
                                    e.target.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--darkPurple)';
                                    e.target.style.color = '#dc3545';
                                  }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: 'white',
                            marginLeft: '8px'
                          }}
                          onClick={() => handleGradeMission(group)}
                          title="Grade this mission"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#28a745';
                            e.target.style.borderColor = '#28a745';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.borderColor = 'var(--darkPurple)';
                          }}
                        >
                          <i className="fas fa-star me-1"></i>
                          Grade
                        </button>
                        <button 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: '#dc3545'
                          }}
                          onClick={() => handleDeleteAllAssignmentsForMission(group.missionId, group.missionName)}
                          title="Delete all assignments for this mission"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Students Tab */}
      <div tabId="students" className={`students-tab-content ${styles.studentsTabContent}`}>
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="student-management-title">Student Management</h4>
              <div>
                <button 
                  className="btn btn-outline-primary me-2" 
                  onClick={() => setShowAddStudentModal(true)}
                  style={{
                    backgroundColor: 'var(--darkOrange)', 
                    color: 'var(--darkPurple)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                >
                  <i className="fas fa-plus"></i> Add Student
                </button>
                <button 
                  className="btn btn-outline-primary me-2" 
                  onClick={() => setShowBulkUploadModal(true)}
                  style={{
                    backgroundColor: 'var(--darkOrange)', 
                    color: 'var(--darkPurple)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                >
                  <i className="fas fa-upload"></i> Bulk Upload
                </button>
                <button 
                  className="btn btn-outline-info me-2" 
                  onClick={handleOpenMessageModal}
                  style={{
                    backgroundColor: 'var(--darkOrange)', 
                    color: 'var(--darkPurple)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                >
                  <i className="fas fa-users"></i> Message
                </button>
                <button 
                  className="btn btn-outline-success me-2" 
                  onClick={() => setShowDownloadCredentialsModal(true)}
                  style={{
                    backgroundColor: 'var(--darkPurple)', 
                    color: 'var(--darkOrange)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                >
                  <i className="fas fa-download"></i> Download Credentials
                </button>
                {generatedCSVData && (
                  <button 
                    className="btn btn-outline-success" 
                    onClick={downloadGeneratedCSV}
                    style={{
                      backgroundColor: 'var(--darkPurple)', 
                      color: 'var(--darkOrange)', 
                      border: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--darkOrange)';
                      e.target.style.color = 'var(--darkPurple)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--darkPurple)';
                      e.target.style.color = 'var(--darkOrange)';
                    }}
                  >
                    <i className="fas fa-download"></i> Download Student Credentials
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Year Group Filter */}
        {students.length > 0 && (
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="yearGroupFilter" className="form-label">Filter by Year Group</label>
              <select 
                id="yearGroupFilter"
                className="form-select"
                value={selectedYearGroup}
                onChange={(e) => setSelectedYearGroup(e.target.value)}
                style={{ 
                  maxWidth: '250px', 
                  height: '32px',
                  fontSize: '14px',
                  padding: '4px 8px'
                }}
              >
                <option value="all">All year groups ({students.length} students)</option>
        
                {yearGroups.map(grade => (
                  <option key={grade} value={grade}>
                    {getYearGroupDisplayName(grade)} ({students.filter(s => getStudentGrade(s) === grade).length} students)
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {getFilteredStudents().length === 0 ? (
          <div className="text-center">
            {students.length === 0 ? (
              <>
                <p>No students found. Add students to your class to get started!</p>
                <button 
                  className="btn btn-primary me-2" 
                  onClick={() => setShowAddStudentModal(true)}
                  style={{
                    backgroundColor: 'var(--darkOrange)', 
                    color: 'var(--darkPurple)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                >
                  Add Student
                </button>
                <button 
                  className="btn btn-outline-primary" 
                  onClick={() => setShowBulkUploadModal(true)}
                  style={{
                    backgroundColor: 'var(--darkOrange)', 
                    color: 'var(--darkPurple)', 
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                >
                  Bulk Upload
                </button>
              </>
            ) : (
              <p>No students found in the selected year group.</p>
            )}
          </div>
        ) : (
          <>
            {/* Pagination Controls */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="studentsPerPage" className="form-label me-2 mb-0">Students per page:</label>
                  <select 
                    id="studentsPerPage"
                    className="form-select"
                    value={studentsPerPage}
                    onChange={(e) => handleStudentsPerPageChange(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((currentPage - 1) * studentsPerPage) + 1} to {Math.min(currentPage * studentsPerPage, getFilteredStudents().length)} of {getFilteredStudents().length} students
                </small>
              </div>
            </div>

            {/* Student Cards */}
            <div className={`container ${styles.studentsContainer}`}>
              <div className={styles.studentsGrid}>
                {getPaginatedStudents().map((student) => (
                <div key={student._id} className={styles.studentCard}>
                  <div className={styles.card}>
                    <div className={styles.cardBody}>
                      <div className={styles.cardHeader}>
                        {getYearGroupDisplayName(getStudentGrade(student)) && (
                          <span className={styles.gradeBadge}>{getYearGroupDisplayName(getStudentGrade(student))}</span>
                        )}
                        <h5 className={styles.studentName}>{student.contactName}</h5>
                      </div>
                      <div className={styles.cardContent}>
                        <p>
                          <strong>Student Code:</strong> 
                          <span className="text-info fw-bold ms-1">{student.studentCode}</span>
                          <br/>
                          <strong>Email:</strong>
                          <span className={`${styles.cardValue} text-info fw-bold ms-1`}>{student.email || ''}</span>
                          <br/>
                          <strong>Username:</strong> 
                          <span className="text-info fw-bold ms-1">{student.username || 'Not set'}</span>
                          <br/>
                          <strong>Status:</strong> 
                          <span className={`badge ${student.status === 'active' ? 'bg-success' : 'bg-warning'} ms-1`}>
                            {student.status}
                          </span>
                          <br/>
                          <strong>Password:</strong> 
                          <span className="text-info fw-bold ms-1">{student.password || 'Not set'}</span>
                        </p>
                      </div>
                      <div className={styles.cardFooter}>
                        <button 
                          className={styles.progressButton}
                          onClick={() => handleViewStudentProgress(student)}
                        >
                          View Progress
                        </button>
                        <button 
                          className={styles.messageButton}
                          onClick={() => handleMessageStudent(student)}
                        >
                          Message
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>

            {/* Pagination Navigation */}
            {getTotalPages() > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Student pagination">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          backgroundColor: 'var(--lightPurple)',
                          color: 'var(--darkOrange)',
                          border: '1px solid var(--lightPurple)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = 'var(--darkOrange)';
                            e.target.style.color = 'var(--darkPurple)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = 'var(--lightPurple)';
                            e.target.style.color = 'var(--darkOrange)';
                          }
                        }}
                      >
                        Previous
                      </button>
                    </li>
                    
                    {/* Page numbers */}
                    {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                        style={{
                          backgroundColor: 'var(--lightPurple)',
                          color: 'var(--darkOrange)',
                          border: '1px solid var(--lightPurple)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = 'var(--darkOrange)';
                            e.target.style.color = 'var(--darkPurple)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = 'var(--lightPurple)';
                            e.target.style.color = 'var(--darkOrange)';
                          }
                        }}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      {/* Messages Tab */}
      <div tabId="messages" className="messages-tab-content">
        <TeacherMessagesTab
          sentMessages={sentMessages}
          onViewMessage={handleViewSentMessage}
          selectedMessage={selectedSentMessage}
          showMessageModal={showSentMessageModal}
          closeMessageModal={closeSentMessageModal}
          tabId="messages"
          tabLabel="Messages"
        />
      </div>

      {/* Assigned Quizzes Tab */}
      <div tabId="assignedquizzes" className={`assigned-quizzes-tab-content ${styles.lessonsTabContent}`}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Assigned Quizzes</h4>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={fetchAssignedQuizzes}
            disabled={loadingAssignedQuizzes}
          >
            {loadingAssignedQuizzes ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingAssignedQuizzes ? (
          <div className="text-center">
            <p>Loading assigned quizzes...</p>
          </div>
        ) : assignedQuizzes.length === 0 ? (
          <div className="text-center">
            <p>No quizzes have been assigned yet.</p>
            <p className="text-muted">Go to the Quizzes tab to assign quizzes to your students.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px',
            padding: '0 15px'
          }}>
            {getGroupedQuizAssignments().map((group) => (
              <div key={group.quizId}>
                <div className="card h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                      <h5 className="card-title text-primary">
                        {group.quizName}
                      </h5>
                      {group.quizDescription && (
                        <p className="card-text text-muted small">
                          {group.quizDescription.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Students:</strong>
                        <span className="badge bg-info">
                          {group.assignments.length} assigned
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Assigned:</strong>
                        <span className="text-muted">
                          {new Date(group.earliestAssignment).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <strong>Status:</strong>
                        <div className="d-flex gap-1">
                          {group.statusCounts.completed > 0 && (
                            <span className="badge bg-success">{group.statusCounts.completed} completed</span>
                          )}
                          {group.statusCounts.in_progress > 0 && (
                            <span className="badge bg-warning">{group.statusCounts.in_progress} in progress</span>
                          )}
                          {group.statusCounts.assigned > 0 && (
                            <span className="badge bg-secondary">{group.statusCounts.assigned} assigned</span>
                          )}
                          {group.statusCounts.overdue > 0 && (
                            <span className="badge bg-danger">{group.statusCounts.overdue} overdue</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Student List */}
                      <div className="mb-3">
                        <strong>Assigned Students:</strong>
                        <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                          {group.assignments.map((assignment) => (
                            <div key={assignment._id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <span className="small">
                                {assignment.studentId?.firstName && assignment.studentId?.lastName 
                                  ? `${assignment.studentId.firstName} ${assignment.studentId.lastName}`
                                  : assignment.studentId?.contactName || ''
                                }{assignment.studentId?.yearGrade && ` (Year ${assignment.studentId.yearGrade})`}
                              </span>
                              <div className="d-flex gap-1">
                                <span className={`badge badge-sm ${
                                  assignment.status === 'completed' ? 'bg-success' :
                                  assignment.status === 'in_progress' ? 'bg-warning' :
                                  assignment.status === 'overdue' ? 'bg-danger' :
                                  'bg-secondary'
                                }`}>
                                  {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1).replace('_', ' ')}
                                </span>
                                <button 
                                  className="btn btn-sm"
                                  onClick={() => handleViewStudentWork(assignment._id, 'quiz')}
                                  style={{
                                    backgroundColor: 'var(--darkPurple)',
                                    borderColor: 'var(--darkPurple)',
                                    color: 'white',
                                    marginLeft: '8px',
                                    fontSize: '0.7rem',
                                    padding: '0.3rem 0.4rem',
                                    minWidth: 'auto',
                                    width: 'auto'
                                  }}
                                  title="View student work"
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#28a745';
                                    e.target.style.borderColor = '#28a745';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--darkPurple)';
                                    e.target.style.borderColor = 'var(--darkPurple)';
                                  }}
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  View
                                </button>
                                <button 
                                  className="btn btn-sm"
                                  onClick={() => handleDeleteQuizAssignment(assignment._id, group.quizName, 
                                    assignment.studentId?.firstName && assignment.studentId?.lastName 
                                      ? `${assignment.studentId.firstName} ${assignment.studentId.lastName}`
                                      : assignment.studentId?.contactName || ''
                                  )}
                                  style={{
                                    backgroundColor: 'var(--darkPurple)',
                                    borderColor: 'var(--darkPurple)',
                                    color: '#dc3545',
                                    fontSize: '0.7rem',
                                    padding: '0.3rem 0.4rem',
                                    minWidth: 'auto',
                                    width: 'auto'
                                  }}
                                  title="Delete assignment"
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#dc3545';
                                    e.target.style.borderColor = '#dc3545';
                                    e.target.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--darkPurple)';
                                    e.target.style.borderColor = 'var(--darkPurple)';
                                    e.target.style.color = '#dc3545';
                                  }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-auto">
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleGradeQuiz(group)}
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: 'white',
                            marginLeft: '8px'
                          }}
                          title="Grade this quiz"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#28a745';
                            e.target.style.borderColor = '#28a745';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.borderColor = 'var(--darkPurple)';
                          }}
                        >
                          <i className="fas fa-star me-1"></i>
                          Grade
                        </button>
                        <button 
                          className="btn btn-sm"
                          onClick={() => handleDeleteAllAssignmentsForQuiz(group.quizId, group.quizName)}
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkPurple)',
                            color: '#dc3545'
                          }}
                          title="Delete all assignments for this quiz"
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.borderColor = '#dc3545';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.borderColor = 'var(--darkPurple)';
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quizzes Tab */}
      <div tabId="quizzes" className="quizzes-tab-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Quiz Management</h4>
          <div className="quiz-actions">
            <Button 
              variant="primary" 
              onClick={() => setShowQuizCreationModal(true)}
              className="me-2"
            >
              Create New Quiz
            </Button>
            <Button 
              variant="outline-primary"
              onClick={() => setActiveTab('library')}
            >
              View Library
            </Button>
          </div>
        </div>
        <QuizManagement students={students} yearGroups={yearGroups} />
      </div>

      {/* Content Library Tab */}
      <div tabId="library" className="library-tab-content">
        <ContentLibrary />
      </div>

      {/* Progress Tracking Tab */}
      <div tabId="progress" className="progress-tab-content">
        <ProgressDashboard />
      </div>

      {/* Curriculum Upload Tab */}
      <div tabId="curriculumupload" className="curriculum-upload-tab-content">
        <div className="teacher-curriculum-matcher-card">
          <div className="card-body">
            <h4 className="card-title">Curriculum Matcher</h4>
            <p className="card-text">Upload your curriculum topics (e.g., 'fractions', 'Newton's laws') or entire curriculum files to get matched Black History content and puzzle missions.</p>
                
                <form className="teacher-curriculum-matcher-form" onSubmit={async (e) => {
                  e.preventDefault();
                  
                  if (!curriculumTopic.trim() && !curriculumFile && !curriculumLessonDescription.trim()) {
                    alert('Please enter a curriculum topic, upload a file, or select a lesson description.');
                    return;
                  }
                  
                  setUploadingCurriculum(true);
                  
                  try {
                    // Create form data for the AI Agent
                    const formData = new FormData();
                    
                    // Ask for a Scheme of Work: Subject, Year Group, Time Allocation, Terms, Units with Curriculum links and Lesson breakdown
                    const schemeInstruction = `Produce a Scheme of Work in this structure:
- Section 1 (then ---): Title "[Subject] – Scheme of Work", Subject, Year Group, Time Allocation, one short intro line.
- Section 2 (then ---): [Subject] Scheme of Work, then terms (e.g. Autumn Term, Spring Term) with Units. Each unit: "Unit N: [Topic] (X lessons)", "Curriculum links" (bullet list), "Lesson breakdown" (numbered list 1. 2. 3. ...).
- Section 3: Remaining term(s) (e.g. Summer Term) with the same unit structure.
Use "---" on its own line between the three sections. You may weave Black History into curriculum links or lesson titles where relevant.`;
                    let message = '';
                    if (curriculumTopic.trim() && curriculumSubject && curriculumGrade) {
                      message = `Create a Scheme of Work for this curriculum. Subject: ${curriculumSubject}, Year Group: ${curriculumGrade}, Topic: ${curriculumTopic}. ${schemeInstruction}`;
                      if (curriculumLessonDescription.trim()) message += `\n\nLesson context: ${curriculumLessonDescription}`;
                    } else if (curriculumLessonDescription.trim() && !curriculumFile) {
                      message = `Create a Scheme of Work from this lesson description. ${curriculumLessonDescription}${curriculumSubject ? ` Subject: ${curriculumSubject}` : ''}${curriculumGrade ? ` Year Group: ${curriculumGrade}` : ''}. ${schemeInstruction}`;
                    } else if (curriculumFile) {
                      message = `Read the uploaded curriculum document and produce a Scheme of Work. Extract the subject, year group, and every unit from the document. Include every unit: if the document has 4 units, output all 4; if 5 or 6, output all. Do not omit or truncate units. Structure the output as: Section 1 – title, Subject, Year Group, Time Allocation, intro; Section 2 – terms and units with Curriculum links and Lesson breakdown; Section 3 – remaining terms/units. Use "---" on its own line between the three sections. ${curriculumSubject ? `Subject: ${curriculumSubject}. ` : ''}${curriculumGrade ? `Year Group: ${curriculumGrade}. ` : ''}${curriculumTopic.trim() ? `Topic: ${curriculumTopic}. ` : ''}${schemeInstruction}`;
                    }
                    
                    formData.append('message', message);
                    
                    if (curriculumFile) {
                      formData.append('files', curriculumFile);
                    }

                    // Use the existing AI Agent endpoint
                    const response = await axios.post(createApiUrl('/ai-agent'), formData, {
                      withCredentials: true
                    });

                    // Extract the AI response
                    let messageContent = null;
                    
                    if (response.data && response.data.reply) {
                      messageContent = response.data.reply;
                    } else if (response.data && response.data.message) {
                      messageContent = response.data.message;
                    } else if (response.data && typeof response.data === 'string') {
                      messageContent = response.data;
                    } else if (response.data && response.data.response) {
                      messageContent = response.data.response;
                    } else if (response.data && response.data.content) {
                      messageContent = response.data.content;
                    } else if (response.data && response.data.text) {
                      messageContent = response.data.text;
                    } else if (response.data && response.data.answer) {
                      messageContent = response.data.answer;
                    } else if (response.data && response.data.reply) {
                      messageContent = response.data.reply;
                    } else if (response.data && response.data.result) {
                      messageContent = response.data.result;
                    } else if (response.data && response.data.data) {
                      messageContent = response.data.data;
                    }
                    
                    if (messageContent) {
                      
                      // Clean the AI response by removing HTML tags and formatting properly
                      const cleanContent = typeof messageContent === 'string' 
                        ? messageContent
                            .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to newlines
                            .replace(/<\/?[^>]+(>|$)/g, '')  // Remove any other HTML tags
                            .replace(/\n\s*\n/g, '\n\n')     // Clean up multiple newlines
                            .trim()
                        : messageContent;

                      // Scheme of work: always display as three sections with two horizontal lines
                      const formattedContent = typeof cleanContent === 'string'
                        ? formatAsSchemeOfWork(cleanContent)
                        : cleanContent;
                      
                      // Extract historical content if present
                      const historicalContent = response.data?.historicalContent;
                      if (historicalContent && historicalContent.id) {
                        // Store it for use when creating missions
                        localStorage.setItem('matchedHistoricalContentId', historicalContent.id);
                        // Store the full historical content data for display
                        setLatestHistoricalContent(historicalContent);
                      } else {
                        // Clear historical content if not present
                        setLatestHistoricalContent(null);
                      }
                      
                      // Create user message based on what was provided
                      let userMessage = '';
                      if (curriculumFile) {
                        userMessage = `Curriculum File Upload: ${curriculumFile.name}`;
                        if (curriculumSubject) userMessage += ` - ${curriculumSubject}`;
                        if (curriculumGrade) userMessage += ` - ${curriculumGrade}`;
                        if (curriculumTopic.trim()) userMessage += ` - ${curriculumTopic}`;
                        if (curriculumLessonDescription.trim()) userMessage += ` - Lesson: ${curriculumLessonDescription.substring(0, 50)}...`;
                      } else {
                        userMessage = `Curriculum Topic: ${curriculumSubject} - ${curriculumGrade} - ${curriculumTopic}`;
                        if (curriculumLessonDescription.trim()) userMessage += ` - Lesson: ${curriculumLessonDescription.substring(0, 50)}...`;
                      }
                      
                      // Add the curriculum matching to the AI Agent messages
                      setMessages(prev => [
                        ...prev, 
                        { type: 'user', content: userMessage },
                        { type: 'ai', content: formattedContent }
                      ]);
                      
                      // Clear form except Subject and Grade so they carry over to Matched Content
                      setCurriculumTopic('');
                      setCurriculumLessonDescription('');
                      setCurriculumFile(null);
                      if (curriculumFileInputRef.current) {
                        curriculumFileInputRef.current.value = '';
                      }
                      
                      // Switch to AI Agent tab to show the results
                      setLatestCurriculumMatch(formattedContent);
                      
                      // Store clean content for mission creation (but individual sections will override this)
                      localStorage.setItem('cleanMissionContent', cleanContent);
                      
                      // Navigate to Matched Content tab immediately
                      setTimeout(() => {
                        const tabButtons = document.querySelectorAll('.nav-link');
                        const matchedContentTab = Array.from(tabButtons).find(button => 
                          button.textContent.includes('Matched Content')
                        );
                        if (matchedContentTab) {
                          matchedContentTab.click();
                        }
                      }, 100);
                      
                      // Show success toast after navigation
                      setTimeout(() => {
                        showToast(response.data.fromCache
                          ? 'Loaded from cache. Redirecting to Matched Content...'
                          : 'Curriculum matched successfully! Redirecting to Matched Content...', 'success', 3000);
                      }, 200);
                    } else {
                      showToast('Received response but couldn\'t extract content. Please try again.', 'error');
                    }
                    
                  } catch (error) {
                                        showToast('Error matching curriculum. Please try again.', 'error');
                  } finally {
                    setUploadingCurriculum(false);
                  }
                }}>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="curriculumSubject" className="form-label">Academic Subject</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                          <select 
                            id="curriculumSubject"
                            className="form-select"
                            value={curriculumSubject}
                            onChange={(e) => {
                              if (e.target.value === '__add_new__') {
                                setShowAddSubjectInput(true);
                                setCurriculumSubject('');
                              } else {
                                setCurriculumSubject(e.target.value);
                              }
                            }}
                            style={{ flex: 1 }}
                          >
                            <option value="">Select one</option>
                            {availableSubjects.map(subject => (
                              <option key={subject} value={subject}>{subject}</option>
                            ))}
                            <option value="__add_new__">+ Add New Subject</option>
                          </select>
                        </div>
                        {showAddSubjectInput && (
                          <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter new subject name"
                              value={newSubjectName}
                              onChange={(e) => setNewSubjectName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddSubject();
                                }
                              }}
                              style={{ flex: 1 }}
                            />
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={handleAddSubject}
                              disabled={addingSubject || !newSubjectName.trim()}
                            >
                              {addingSubject ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setShowAddSubjectInput(false);
                                setNewSubjectName('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="curriculumGrade" className="form-label">Grade Level</label>
                        <select 
                          id="curriculumGrade"
                          className="form-select"
                          value={curriculumGrade}
                          onChange={(e) => setCurriculumGrade(e.target.value)}
                        >
                          <option value="">Select your grade</option>
                          {GRADE_LEVELS.map(grade => (
                            <option key={grade.value} value={grade.value}>{grade.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="curriculumTopic" className="form-label">Curriculum Topic (Optional)</label>
                      <input 
                        type="text"
                        id="curriculumTopic"
                        className="form-control"
                        value={curriculumTopic}
                        onChange={(e) => setCurriculumTopic(e.target.value)}
                        placeholder="e.g., fractions, Newton's laws, photosynthesis, Civil War, poetry"
                        style={{ color: 'var(--darkOrange)' }}
                      />
                      <div className="form-text">Enter a specific topic OR upload a curriculum file</div>
                    </div>
                    
                    <div className="col-md-6">
                      <label htmlFor="curriculumLessonDescription" className="form-label">Lesson Description (Optional)</label>
                      <div className="input-group">
                        <textarea
                          id="curriculumLessonDescription"
                          className="form-control"
                          value={curriculumLessonDescription}
                          onChange={(e) => setCurriculumLessonDescription(e.target.value)}
                          placeholder="Enter lesson description or select from available lessons..."
                          rows="3"
                          style={{ color: 'var(--darkOrange)' }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleFindLessons}
                          disabled={loadingLessons}
                        >
                          {loadingLessons ? 'Loading...' : 'Find Lessons'}
                        </button>
                      </div>
                      <div className="form-text">Select from available lessons or enter your own description</div>
                    </div>
                  </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">
                        <strong>Upload Curriculum File</strong> (PDF, DOCX, TXT, PPT, PPTX)
                      </label>
                      
                      {/* Drag and Drop File Upload Area */}
                      <DragDropFileUpload
                        onFileSelected={handleCurriculumFileSelect}
                        onFileRemoved={handleCurriculumFileRemove}
                        selectedFile={curriculumFile}
                        accept=".pdf,.docx,.txt,.ppt,.pptx"
                        maxFileSize={10 * 1024 * 1024} // 10MB
                        supportedTypes={['.pdf', '.docx', '.txt', '.ppt', '.pptx']}
                        placeholder="Click to browse or drag & drop here"
                        description="Supports: PDF, DOCX, TXT, PPT, PPTX"
                      />
                      
                      <div className="form-text mt-2">
                        Upload your entire curriculum document for comprehensive Black History content matching
                      </div>
                    </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary mb-3"
                    disabled={uploadingCurriculum}
                  >
                    {uploadingCurriculum ? 'Analyzing...' : 'Match Curriculum'}
                  </button>
                  
                </form>
                
                {/* How it works section - full width outside the form */}
                <div className="alert alert-info mt-3" style={{ width: '100%', maxWidth: '100%', marginLeft: '0', marginRight: '0' }}>
                  <strong>How it works:</strong><br/>
                  • Enter a single topic (e.g., "fractions") for quick matching<br/>
                  • OR upload an entire curriculum file for comprehensive analysis<br/>
                  • OR select from available lessons to use their descriptions<br/>
                  • The AI Agent will analyze your content and provide matched Black History resources and puzzle missions
                </div>
              </div>
            </div>
          </div>


      {/* Matched Content Tab */}
      <div tabId="matchedcontent" className="matched-content-tab-content">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Matched Content & Puzzle Missions</h4>
                <p className="card-text">View Black History content and puzzle missions that have been matched to your curriculum topics.</p>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Subject</label>
                    <div className="form-control-plaintext border rounded px-2 py-1 bg-light" style={{ minHeight: 38 }}>
                      {curriculumSubject || '—'}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Year/Group</label>
                    <div className="form-control-plaintext border rounded px-2 py-1 bg-light" style={{ minHeight: 38 }}>
                      {curriculumGrade || '—'}
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="contentFilter" className="form-label">Filter by Subject</label>
                  <select 
                    id="contentFilter"
                    className="form-select"
                    value={contentFilter}
                    onChange={(e) => setContentFilter(e.target.value)}
                    style={{ maxWidth: '200px' }}
                  >
                    <option value="all">All Subjects</option>
                    {availableSubjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                
                <div className="matched-content-grid">
                  {assets.filter(asset => 
                    asset.type === 'curriculum' && 
                    asset.matchedContent && 
                    asset.matchedContent.length > 0 && 
                    (contentFilter === 'all' || asset.category === contentFilter)
                  ).map((content) => (
                    <div key={content._id} className="content-card">
                      <h5 className="card-title">{content.name}</h5>
                      <p className="card-content">{content.description}</p>
                      
                      {content.matchedContent && content.matchedContent.length > 0 && (
                        <div className="matched-items">
                          <h6>Matched Black History Resources:</h6>
                          <ul>
                            {content.matchedContent.map((item, index) => (
                              <li key={index}>
                                <strong>{item.title}</strong> - {item.description}
                                {item.url && (
                                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary ms-2">
                                    View
                                  </a>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="d-flex justify-content-between">
                        <button className="btn btn-primary">View Details</button>
                        <button className="btn btn-outline-success">Assign to Class</button>
                        <button className="btn btn-outline-warning">Create Puzzle Mission</button>
                      </div>
                    </div>
                  ))}


                </div>

                {latestCurriculumMatch && (
                  <div className="matched-curriculum-response scheme-of-work-container" style={{ background: '#fff', color: '#000', borderRadius: 8, padding: 20, marginBottom: 20 }} onClick={handleSchemeContentClick}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Scheme of Work</h5>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setLatestCurriculumMatch(null);
                          setLatestHistoricalContent(null);
                          showToast('Matched content cleared successfully!', 'success');
                        }}
                        title="Clear matched content"
                      >
                        🗑️ Clear
                      </button>
                    </div>
                    
                    {/* Show full scheme of work first (main response) */}
                    {(() => {
                      const displayContent = latestCurriculumMatch;
                      const isSchemeOfWork = typeof displayContent === 'string' &&
                        displayContent.includes('Scheme of Work') &&
                        (displayContent.includes('Time Allocation') || displayContent.includes('Curriculum links') || displayContent.includes('Lesson breakdown'));
                      const topicRegex = /(\d+\.\s+[^<]+)([\s\S]*?)(?=\d+\.\s+[^<]+|Curriculum Analysis:|$)/gi;
                      const matches = isSchemeOfWork ? [] : [...(displayContent || '').matchAll(topicRegex)];

                      if (isSchemeOfWork || matches.length === 0) {
                        const stripHtml = (str) => (str || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
                        const { intro, units } = splitSchemeIntoUnits(latestCurriculumMatch);
                        if (units.length > 0) {
                          return (
                            <>
                              {intro && (
                                <div className="scheme-of-work-content" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginBottom: 20 }}>
                                  <div dangerouslySetInnerHTML={{ __html: intro }} />
                                </div>
                              )}
                              {units.map((unit, idx) => (
                                <div key={idx} className="scheme-unit-block" style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
                                  <div className="scheme-of-work-content" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginBottom: 12 }}>
                                    <div dangerouslySetInnerHTML={{ __html: unit.content }} />
                                  </div>
                                  <div className="d-flex gap-2 mt-2">
                                    <button className="btn btn-outline-success btn-sm" onClick={() => openMissionFormWithPrefill(stripHtml(unit.content))}>
                                      🚀 Create Mission
                                    </button>
                                    <button className="btn btn-outline-primary btn-sm" onClick={() => {
                                      setCurriculumData({ content: stripHtml(unit.content), subject: curriculumSubject, gradeLevel: curriculumGrade });
                                      setShowQuizCreationModal(true);
                                    }}>
                                      ❓ Create Quiz
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </>
                          );
                        }
                        return (
                          <>
                            <div className="scheme-of-work-content" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginBottom: 20 }}>
                              <div dangerouslySetInnerHTML={{ __html: latestCurriculumMatch }} />
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <button className="btn btn-outline-success" onClick={() => openMissionFormWithPrefill(stripHtml(latestCurriculumMatch))}>🚀 Create Mission</button>
                              <button className="btn btn-outline-primary" onClick={() => { setCurriculumData({ content: stripHtml(latestCurriculumMatch), subject: curriculumSubject, gradeLevel: curriculumGrade }); setShowQuizCreationModal(true); }}>❓ Create Quiz</button>
                            </div>
                          </>
                        );
                      }
                      
                      // Show each topic with its own Create Mission button
                      return matches.map((m, idx) => {
                        const topicTitle = m[1].trim(); // The topic title (e.g., "1. Locational Knowledge")
                        const topicContent = m[2].trim(); // The content for that topic

                        // More flexible content filtering - look for various mission-related keywords
                        const hasMissionContent = topicContent.includes('Puzzle Mission') || 
                                                topicContent.includes('Mission Title') || 
                                                topicContent.includes('Learning Objective') ||
                                                topicContent.includes('Instructions') ||
                                                topicContent.includes('Black History Connections');
                        
                        if (topicContent.length < 30 || !hasMissionContent) {
                          return null;
                        }
                        
                        return (
                          <div key={idx} style={{ 
                            marginBottom: 24, 
                            borderBottom: '1px solid #eee', 
                            paddingBottom: 12,
                            background: '#f8f9fa',
                            padding: '15px',
                            borderRadius: '8px'
                          }}>
                            <div style={{ 
                              fontWeight: 600, 
                              marginBottom: 12, 
                              color: '#2c3e50',
                              fontSize: '16px',
                              borderBottom: '2px solid #007bff',
                              paddingBottom: '8px'
                            }}>
                              {topicTitle}
                            </div>
                            <div style={{ 
                              whiteSpace: 'pre-line', 
                              marginBottom: 15,
                              lineHeight: '1.6',
                              color: '#34495e'
                            }}>
                              {topicContent}
                            </div>
                            <div className="d-flex gap-2">
                              <button 
                                className="btn btn-outline-success" 
                                onClick={() => {
                                  const missionContent = `${topicTitle}: ${topicContent}`;
                                  openMissionFormWithPrefill(missionContent);
                                }}
                                style={{
                                  fontWeight: '500',
                                  padding: '8px 16px'
                                }}
                              >
                                🚀 Create Mission
                              </button>
                              <button 
                                className="btn btn-outline-primary" 
                                onClick={() => {
                                  setCurriculumData({
                                    content: `${topicTitle}: ${topicContent}`,
                                    subject: curriculumSubject,
                                    gradeLevel: curriculumGrade
                                  });
                                  setShowQuizCreationModal(true);
                                }}
                                style={{
                                  fontWeight: '500',
                                  padding: '8px 16px'
                                }}
                              >
                                ❓ Create Quiz
                              </button>
                            </div>
                          </div>
                        );
                      }).filter(Boolean); // Remove null entries
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lesson plans Tab – saved lesson plans (same data as Cache → Lesson plans) */}
      <div tabId="lessonplans" className="lessonplans-tab-content">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Lesson plans</h4>
                <p className="card-text">Lesson plans you generate from the scheme of work are saved here. Use <strong>Save</strong> in the lesson plan modal to add or update. Click a row to view or copy.</p>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted">{lessonPlanEntries.length} saved lesson plan{lessonPlanEntries.length !== 1 ? 's' : ''}</span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowClearAllLessonPlansModal(true)}
                    disabled={lessonPlanEntries.length === 0 || lessonPlanCacheLoading}
                  >
                    Clear all
                  </button>
                </div>
                {lessonPlanCacheLoading ? (
                  <p className="text-muted">Loading…</p>
                ) : lessonPlanEntries.length === 0 ? (
                  <p className="text-muted mb-0">No saved lesson plans yet. Go to <strong>Curriculum Matcher</strong> → <strong>Matched Content</strong>, click a lesson breakdown line to generate a lesson plan, then click <strong>Save</strong> to add it here.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>Year</th>
                          <th>Title</th>
                          <th>Date</th>
                          <th style={{ width: 80 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lessonPlanEntries.map((e) => (
                          <tr
                            key={e.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleViewLessonPlan(e.id)}
                            onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); handleViewLessonPlan(e.id); } }}
                          >
                            <td>{e.subject || '—'}</td>
                            <td>{e.yearGroup || '—'}</td>
                            <td className="fw-medium">{e.lessonTitle || 'Lesson plan'}</td>
                            <td>{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : '—'}</td>
                            <td onClick={(ev) => ev.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => openDeleteLessonPlanModal(e)}
                                title="Delete lesson plan"
                                aria-label="Delete lesson plan"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cache Tab (sub-tabs: Curriculum cache | Lesson plans) */}
      <div tabId="cache" className="cache-tab-content">
        <div className="row">
          <div className="col-md-12">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${cacheSubTab === 'curriculum' ? 'active' : ''}`}
                  onClick={() => setCacheSubTab('curriculum')}
                >
                  Curriculum cache
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${cacheSubTab === 'lessonplans' ? 'active' : ''}`}
                  onClick={() => setCacheSubTab('lessonplans')}
                >
                  Lesson plans
                </button>
              </li>
            </ul>
            {cacheSubTab === 'curriculum' && (
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title">Curriculum cache</h4>
                  <p className="card-text">When you upload a curriculum document and match it, the response is cached. Uploading the same document again returns the cached response without re-analysing.</p>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">{cacheEntries.length} cached document{cacheEntries.length !== 1 ? 's' : ''}</span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowClearCurriculumCacheModal(true)}
                      disabled={cacheEntries.length === 0 || cacheLoading}
                    >
                      Clear cache
                    </button>
                  </div>
                  {cacheLoading ? (
                    <p className="text-muted">Loading…</p>
                  ) : cacheEntries.length === 0 ? (
                    <p className="text-muted mb-0">No cached documents. Upload and match a curriculum file to create a cache entry.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {cacheEntries.map((e) => (
                        <li key={e.id || e.contentHash} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <span className="fw-medium">{e.filename || 'Document'}</span>
                            <span className="text-muted small ms-2">{e.contentHash ? `${e.contentHash.slice(0, 12)}…` : ''}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : ''}</span>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => openDeleteCurriculumCacheModal(e)}
                              title="Delete this cache entry"
                              aria-label="Delete this cache entry"
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {cacheSubTab === 'lessonplans' && (
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title">Lesson plans</h4>
                  <p className="card-text">Lesson plans generated from your scheme of work are cached here. Same lesson + subject + year group is reused. View or manage plans from the <strong>Lesson plans</strong> tab.</p>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">{lessonPlanEntries.length} cached lesson plan{lessonPlanEntries.length !== 1 ? 's' : ''}</span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowClearAllLessonPlansModal(true)}
                      disabled={lessonPlanEntries.length === 0 || lessonPlanCacheLoading}
                    >
                      Clear cache
                    </button>
                  </div>
                  {lessonPlanCacheLoading ? (
                    <p className="text-muted">Loading…</p>
                  ) : lessonPlanEntries.length === 0 ? (
                    <p className="text-muted mb-0">No cached lesson plans. In Curriculum Matcher → Matched Content, click any lesson breakdown line to generate and save a lesson plan.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {lessonPlanEntries.map((e) => (
                        <li
                          key={e.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <span className="fw-medium">{e.lessonTitle || 'Lesson plan'}</span>
                            {(e.subject || e.yearGroup) && (
                              <span className="text-muted small ms-2">{[e.subject, e.yearGroup].filter(Boolean).join(' · ')}</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : ''}</span>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => openDeleteLessonPlanModal(e)}
                              title="Delete cache entry"
                              aria-label="Delete cache entry"
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Agent Tab */}
      <div tabId="aiagent" className="aiagent-tab-content">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="light-orange-text mb-0">Our Black History AI Agent is here to help you!</p>
              {messages.length > 0 && (
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setMessages([]);
                    showToast('AI Agent conversation cleared successfully!', 'success');
                  }}
                  title="Clear conversation history"
                >
                  🗑️ Clear Chat
                </button>
              )}
            </div>
            <div id="messages" className="messages-container" style={{ 
              maxHeight: '400px', 
              overflowY: 'auto', 
              border: '1px solid #ddd', 
              padding: '15px', 
              marginBottom: '20px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px'
            }}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`message ${message.type === 'user' ? 'user-message' : 'ai-message'}`}
                  style={{
                    marginBottom: '20px',
                    padding: '15px',
                    borderRadius: '10px',
                    backgroundColor: message.type === 'user' ? '#e3f2fd' : '#ffffff',
                    color: '#000000',
                    border: message.type === 'user' ? '2px solid #2196f3' : '2px solid #f0f0f0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    position: 'relative'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <strong style={{ color: '#ff6f00', fontSize: '14px' }}>
                      {message.type === 'user' ? 'You:' : 'KBH AI Agent:'}
                    </strong>
                    {message.type === 'ai' && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(message.content.replace(/<br>/g, '\n').replace(/<[^>]*>/g, ''));
                          // Error handling removed for production
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#f0f0f0'
                        }}
                        title="Copy response"
                      >
                        📋 Copy
                      </button>
                    )}
                  </div>
                  <div 
                    dangerouslySetInnerHTML={{ __html: '  ' + message.content }} 
                    style={{ 
                      color: '#000000',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap'
                    }} 
                  />
                </div>
              ))}
            </div>
            <br />
            <form id="aiAgentForm" encType="multipart/form-data" className="ai-agent-form">
              <textarea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Type your message here..."
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  marginBottom: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  color: '#000000',
                  backgroundColor: '#ffffff',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
              <div className="form-controls">
                <DragDropFileUpload
                  onFileSelected={handleAIFileSelect}
                  onFileRemoved={handleAIFileRemove}
                  selectedFile={uploadedFiles.length > 0 ? uploadedFiles[0] : null}
                  accept=".txt,.pdf,.docx"
                  multiple={true}
                  maxFileSize={10 * 1024 * 1024} // 10MB
                  supportedTypes={['.txt', '.pdf', '.docx']}
                  placeholder="Click to upload files or drag and drop"
                  description="Supports: TXT, PDF, DOCX files"
                />
                <div className="button-group">
                  <button 
                    type="button" 
                    className="btn" 
                    onClick={(e) => { e.preventDefault(); sendMessage(); }}
                    style={{
                      backgroundColor: 'var(--lightPurple)',
                      color: 'var(--darkOrange)',
                      border: '2px solid var(--lightPurple)',
                      fontWeight: 'bold',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginRight: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--darkOrange)';
                      e.target.style.color = 'var(--darkPurple)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--lightPurple)';
                      e.target.style.color = 'var(--darkOrange)';
                    }}
                  >
                    Submit
                  </button>
                  <button 
                    type="button" 
                    className="btn speakButton" 
                    onClick={(e) => { e.preventDefault(); startVoiceInput(); }}
                    style={{
                      backgroundColor: 'var(--lightPurple)',
                      color: 'var(--darkOrange)',
                      border: '2px solid var(--lightPurple)',
                      fontWeight: 'bold',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginRight: '10px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = 'var(--darkOrange)';
                      e.target.style.color = 'var(--darkPurple)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'var(--lightPurple)';
                      e.target.style.color = 'var(--darkOrange)';
                    }}
                  >
                    🎤 Speak
                  </button>
                  <button
                    type="button"
                    className={`btn ${isReading ? 'btn-danger' : 'readButton'}`}
                    onClick={toggleReading}
                    style={!isReading ? {
                      backgroundColor: 'var(--lightPurple)',
                      color: 'var(--darkOrange)',
                      border: '2px solid var(--lightPurple)',
                      fontWeight: 'bold',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    } : {
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      if (!isReading) {
                        e.target.style.backgroundColor = 'var(--darkOrange)';
                        e.target.style.color = 'var(--darkPurple)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isReading) {
                        e.target.style.backgroundColor = 'var(--lightPurple)';
                        e.target.style.color = 'var(--darkOrange)';
                      }
                    }}
                  >
                    {isReading ? 'Stop Reading' : 'Read'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </BaseDashboard>

    {/* Video Modal — drag the bottom-right corner to resize */}
    <Modal
      isOpen={showVideoModal && selectedVideo}
      onClose={closeVideoModal}
      className="video-player-modal"
      disableOverlayClose
    >
      <div className="video-player-modal-shell">
        <div className="modal-header video-player-modal-header">
          <div>
            <h3>Video Player</h3>
            <p className="video-player-modal-hint">Drag the corner to resize</p>
          </div>
          <button type="button" className="close-btn" onClick={closeVideoModal} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body video-player-modal-body">
          <video
            key={selectedVideo?.url}
            ref={videoModalRef}
            className="video-player-modal-video"
            controls
            playsInline
            {...restrictedVideoProps}
            src={selectedVideo?.url}
            poster={selectedVideo?.thumbnail || undefined}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </Modal>

    {/* Document Modal */}
    <Modal isOpen={showDocumentModal && selectedDocument} onClose={closeDocumentModal}>
      <div className="modal-header">
        <h3>Document Viewer</h3>
        <button className="close-btn" onClick={closeDocumentModal}>×</button>
      </div>
      <div className="modal-body">
        <iframe 
          src={selectedDocument?.url || selectedDocument?.documentUrl} 
          width="100%" 
          height="500px"
          title="Document Viewer"
        />
      </div>
    </Modal>

    {/* Add Student Modal */}
    <Modal isOpen={showAddStudentModal} onClose={() => setShowAddStudentModal(false)}>
      <div className="modal-header">
        <h3>Add New Student</h3>
        <button className="close-btn" onClick={() => setShowAddStudentModal(false)}>×</button>

      </div>
      <div className="modal-body">
        <form onSubmit={handleAddStudent}>
          <div className="form-group mb-3">
            <label htmlFor="contactName">Full Name *</label>
            <input
              type="text"
              id="contactName"
              className="form-control"
              value={newStudent.contactName}
              onChange={(e) => setNewStudent({...newStudent, contactName: e.target.value})}
              required
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="email">Email (Optional)</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={newStudent.email}
              onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="grade">Grade</label>
            <select
              id="grade"
              className="form-control"
              value={newStudent.grade}
              onChange={(e) => setNewStudent({...newStudent, grade: e.target.value})}
            >
              <option value="">Select your grade</option>
              <option value="K">Reception/Kindergarten</option>
              <option value="1">Primary/Year 1</option>
              <option value="2">Primary/Year 2</option>
              <option value="3">Primary/Year 3</option>
              <option value="4">Primary/Year 4</option>
              <option value="5">Primary/Year 5</option>
              <option value="6">Primary/Year 6</option>
              <option value="7">Year/Grade 7</option>
              <option value="8">Year/Grade 8</option>
              <option value="9">Year/Grade 9</option>
              <option value="10">Year/Grade 10</option>
              <option value="11">Year/Grade 11</option>
              <option value="12">Year/Grade 12</option>
            </select>
          </div>
          <div className="form-group mb-3">
            <label htmlFor="dateOfBirth">Date of Birth *</label>
            <input
              type="date"
              id="dateOfBirth"
              className="form-control"
              value={newStudent.dateOfBirth}
              onChange={(e) => setNewStudent({...newStudent, dateOfBirth: e.target.value})}
              required
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="parentEmail">Parent Email</label>
            <input
              type="email"
              id="parentEmail"
              className="form-control"
              value={newStudent.parentEmail}
              onChange={(e) => setNewStudent({...newStudent, parentEmail: e.target.value})}
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="parentPhone">Parent Phone</label>
            <input
              type="text"
              id="parentPhone"
              className="form-control"
              value={newStudent.parentPhone}
              onChange={(e) => setNewStudent({...newStudent, parentPhone: e.target.value})}
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{backgroundColor: 'var(--darkPurple)', color: 'var(--darkOrange)', border: 'none'}}>Add Student</button>
        </form>
      </div>
    </Modal>

    {/* Bulk Upload Modal */}
    <Modal isOpen={showBulkUploadModal} onClose={() => setShowBulkUploadModal(false)} className="bulk-upload-modal">
      <div className={bulkUploadStyles.modalContent}>
        <div className={bulkUploadStyles.modalHeader}>
          <h3>Bulk Upload Students</h3>
          <button className={bulkUploadStyles.closeBtn} onClick={() => setShowBulkUploadModal(false)}>×</button>
        </div>
        <div className={bulkUploadStyles.modalBody}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="csvFile" className={bulkUploadStyles.formLabel}>Upload CSV File</label>
            <DragDropFileUpload
              onFileSelected={handleCSVFileSelect}
              onFileRemoved={handleCSVFileRemove}
              selectedFile={selectedCSVFile}
              accept=".csv"
              maxFileSize={5 * 1024 * 1024} // 5MB
              supportedTypes={['.csv']}
              placeholder="Click to upload CSV or drag and drop"
              description="CSV format: First Name, Last Name, Email, Year/Grade, Parent Email, Parent Phone"
            />
          </div>
          
          {bulkStudents.length > 0 && (
            <div className={bulkUploadStyles.previewSection}>
              <h6 className={bulkUploadStyles.previewTitle}>Preview Students ({bulkStudents.length} students):</h6>
              <div className={bulkUploadStyles.previewTable}>
                <table className={bulkUploadStyles.table}>
                  <thead>
                    <tr>
                      <th className={bulkUploadStyles.tableHeader}>Name</th>
                      <th className={bulkUploadStyles.tableHeader}>Email</th>
                      <th className={bulkUploadStyles.tableHeader}>Grade</th>
                      <th className={bulkUploadStyles.tableHeader}>Parent Email</th>
                      <th className={bulkUploadStyles.tableHeader}>Parent Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkStudents.map((student, index) => (
                      <tr key={index} className={bulkUploadStyles.tableRow}>
                        <td className={bulkUploadStyles.tableCell}>{student.contactName}</td>
                        <td className={bulkUploadStyles.tableCell}>{student.email}</td>
                        <td className={bulkUploadStyles.tableCell}>{getStudentGrade(student)}</td>
                        <td className={bulkUploadStyles.tableCell}>{student.parentEmail || ''}</td>
                        <td className={bulkUploadStyles.tableCell}>{student.parentPhone || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          
          <div className={bulkUploadStyles.buttonContainer}>
            <button 
              type="button" 
              className={`${bulkUploadStyles.button} ${bulkUploadStyles.buttonPrimary}`}
              onClick={handleBulkUpload}
              disabled={uploadingStudents || bulkStudents.length === 0}
            >
              Add Students
            </button>


            <button 
              type="button" 
              className={`${bulkUploadStyles.button} ${bulkUploadStyles.buttonSecondary}`}
              onClick={() => {
                setBulkStudents([]);
                setSelectedCSVFile(null);
                setGeneratedCSVData(null);
                setShowBulkUploadModal(false);
              }}
              disabled={uploadingStudents}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>

    {/* Download Credentials Modal */}
    <Modal isOpen={showDownloadCredentialsModal} onClose={() => setShowDownloadCredentialsModal(false)} className="download-credentials-modal">
      <div className="modal-header">
        <h3>Download Credentials</h3>
        <button className="close-btn" onClick={() => setShowDownloadCredentialsModal(false)}>×</button>
      </div>
      <div className="modal-body">
        <p className="mb-3">Choose all students or a year group.</p>
        <button
          type="button"
          className="btn btn-primary w-100 mb-3"
          onClick={downloadAllStudentsCSV}
        >
          <i className="fas fa-download me-2"></i>
          Download All ({students?.length || 0} students)
        </button>
        <label className="form-label">Year groups</label>
        <div className="download-credentials-year-list">
          {yearGroups.filter(Boolean).length === 0 && (
            <div className="download-credentials-year-empty">No year groups found.</div>
          )}
          {yearGroups.filter(Boolean).map(yearGroup => {
            const count = (students || []).filter(s => getStudentGrade(s) === yearGroup).length;
            return (
              <button
                key={yearGroup}
                type="button"
                className="btn btn-secondary"
                onClick={() => downloadStudentsByYearGroup(yearGroup)}
              >
                <i className="fas fa-graduation-cap me-2"></i>
                {getYearGroupDisplayName(yearGroup)} ({count})
              </button>
            );
          })}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDownloadCredentialsModal(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>

    {/* Message Modal */}
    <Modal isOpen={showMessageModal} onClose={closeMessageModal} className="send-message-modal">
      <div className="modal-header">
        <h3>
          {messageType === 'single' && (selectedStudent
            ? `Send Message to ${selectedStudent.contactName}`
            : 'Send Message to a Student')}
          {messageType === 'group' && 'Send Message to Selected Students'}
          {messageType === 'all' && 'Send Message to All Students'}
        </h3>
        <button className="close-btn" onClick={closeMessageModal}>×</button>
      </div>
      <div className="modal-body">
        <form onSubmit={handleSendMessage}>
          {!messageFromStudentCard && (
          <>
          <div className="form-group mb-3">
            <label className="form-label">Message Type</label>
            <div className="btn-group w-100" role="group">
              <input
                type="radio"
                className="btn-check"
                name="messageType"
                id="message-type-single"
                checked={messageType === 'single'}
                onChange={() => {
                  setMessageType('single');
                  setSelectedStudents([]);
                  setSelectedYearGroupForMessage('all');
                }}
              />
              <label className="btn btn-outline-primary" htmlFor="message-type-single">
                Single Student
              </label>
              
              <input
                type="radio"
                className="btn-check"
                name="messageType"
                id="message-type-group"
                checked={messageType === 'group'}
                onChange={() => {
                  setMessageType('group');
                  setSelectedStudent(null);
                  setSelectedYearGroupForMessage('all');
                }}
              />
              <label className="btn btn-outline-primary" htmlFor="message-type-group">
                Group
              </label>
              
              <input
                type="radio"
                className="btn-check"
                name="messageType"
                id="message-type-all"
                checked={messageType === 'all'}
                onChange={() => {
                  setMessageType('all');
                  setSelectedStudent(null);
                  setSelectedStudents([]);
                }}
              />
              <label className="btn btn-outline-primary" htmlFor="message-type-all">
                All Students
              </label>
            </div>
          </div>

          {messageType !== 'all' && (
            <div className="form-group mb-3 message-student-picker">
              <label className="form-label" htmlFor="message-year-group-filter">
                Filter by Year Group
              </label>
              <select
                id="message-year-group-filter"
                className="form-select mb-3"
                value={selectedYearGroupForMessage}
                onChange={(e) => {
                  setSelectedYearGroupForMessage(e.target.value);
                  if (messageType === 'single') {
                    setSelectedStudent(null);
                  }
                }}
              >
                <option value="all">All year groups</option>
                {yearGroups.map(grade => (
                  <option key={grade} value={grade}>
                    {getYearGroupDisplayName(grade)}
                  </option>
                ))}
              </select>

              {messageType === 'single' && (
                <>
                  <label className="form-label" htmlFor="message-single-student-select">
                    Select a Student
                  </label>
                  <select
                    id="message-single-student-select"
                    className="form-select"
                    value={selectedStudent?._id ? String(selectedStudent._id) : ''}
                    onChange={(e) => {
                      const student = getStudentsForMessage().find(
                        s => String(s._id) === String(e.target.value)
                      );
                      setSelectedStudent(student || null);
                    }}
                  >
                    <option value="">-- Choose a student --</option>
                    {getStudentsForMessage().map(student => (
                      <option key={String(student._id)} value={String(student._id)}>
                        {student.contactName}
                        {getYearGroupDisplayName(getStudentGrade(student))
                          ? ` (${getYearGroupDisplayName(getStudentGrade(student))})`
                          : ''}
                        {student.email ? ` — ${student.email}` : ''}
                      </option>
                    ))}
                  </select>
                  {getStudentsForMessage().length === 0 && (
                    <div className="mt-2 message-student-picker-empty">
                      No students found for this year group.
                    </div>
                  )}
                </>
              )}

              {messageType === 'group' && (
                <>
                  <label className="form-label">Select Students</label>
                  <div className="message-student-picker-list">
                    {getStudentsForMessage().length === 0 && (
                      <div className="message-student-picker-empty">
                        No students found for this year group.
                      </div>
                    )}
                    {getStudentsForMessage().map(student => (
                      <div key={student._id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`message-student-${student._id}`}
                          checked={selectedStudents.find(s => s._id === student._id) !== undefined}
                          onChange={() => handleStudentSelection(student)}
                        />
                        <label className="form-check-label" htmlFor={`message-student-${student._id}`}>
                          {student.contactName}
                          {getYearGroupDisplayName(getStudentGrade(student)) &&
                            ` (${getYearGroupDisplayName(getStudentGrade(student))})`}
                        </label>
                      </div>
                    ))}
                  </div>
                  {selectedStudents.length > 0 && (
                    <div className="mt-2">
                      <small>Selected: {selectedStudents.length} student(s)</small>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Recipients Summary */}
          <div className="alert alert-info mb-3">
            <strong>Recipients:</strong> {getMessageRecipients().length} student(s)
            {messageType === 'single' && selectedStudent && (
              <div className="mt-1">
                <small>{selectedStudent.contactName}</small>
              </div>
            )}
            {messageType === 'group' && selectedStudents.length > 0 && (
              <div className="mt-1">
                <small>
                  {selectedStudents.map(s => s.contactName).join(', ')}
                </small>
              </div>
            )}
          </div>
          </>
          )}

          <div className="form-group mb-3">
            <label htmlFor="messageText">Message</label>
            <textarea
              id="messageText"
              className="form-control"
              rows="4"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              required
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={closeMessageModal}
              disabled={sendingMessage}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={sendingMessage || !messageText.trim() || 
                (messageType === 'single' && !selectedStudent) ||
                (messageType === 'group' && selectedStudents.length === 0)}
            >
              {sendingMessage ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </Modal>

    {/* Student Progress Monitoring Modal */}
    
    <Modal isOpen={showStudentProgressModal} onClose={handleCloseStudentProgress} className="student-progress-modal">
      <div className="modal-header">
        <h3>Student Progress</h3>
        <button className="close-btn" onClick={handleCloseStudentProgress}>×</button>
      </div>
      <div className="modal-body">
        {monitoringStudent && (
          <StudentProgressMonitoring student={monitoringStudent} />
        )}
      </div>
    </Modal>

    {/* Mission Viewer Modal */}
    <Modal isOpen={showMissionViewerModal} onClose={handleCloseMissionViewer}>
      <div className="modal-body" style={{ padding: 0, maxWidth: '90vw', maxHeight: '90vh' }}>
        {viewingMission && (
          <TeacherMissionViewer 
            mission={viewingMission} 
            onClose={handleCloseMissionViewer} 
          />
        )}
      </div>
    </Modal>

    {/* Delete Confirmation Modal */}
    <Modal isOpen={showDeleteConfirmModal} onClose={() => setShowDeleteConfirmModal(false)}>
      <div className="modal-header">
        <h3>Delete Mission</h3>
        <button className="close-btn" onClick={() => setShowDeleteConfirmModal(false)}>×</button>
      </div>
      <div className="modal-body">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> This action cannot be undone.
        </div>
        <p>
          Are you sure you want to delete the mission <strong>"{missionToDelete?.title}"</strong>?
        </p>
        <p className="text-muted">
          This will permanently remove the mission and all associated data.
        </p>
        
        {/* Show mission status for information */}
        {missionToDelete && (
          <div className="mt-3">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Mission Status:</strong> {missionToDelete.status?.charAt(0).toUpperCase() + missionToDelete.status?.slice(1)}
              <div className="mt-2">
                <small>
                  <strong>Note:</strong> For testing purposes, all missions can be deleted regardless of status.
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <div className="d-flex justify-content-end gap-2">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => setShowDeleteConfirmModal(false)}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger"
            onClick={confirmDeleteMission}
          >
            <i className="fas fa-trash me-2"></i>
            Delete Mission
          </button>
        </div>
      </div>
    </Modal>

    {/* Lesson Selector Modal */}
    {showLessonSelector && (
      <div className={styles['lesson-modal-overlay']} onClick={handleCloseLessonSelector}>
        <div className={styles['lesson-modal-content']} onClick={e => e.stopPropagation()}>
          <div className={styles['lesson-modal-header']}>
            <h2 className={styles['lesson-modal-title']}>Select a Lesson</h2>
            <button
              type="button"
              className={styles['lesson-modal-close']}
              onClick={handleCloseLessonSelector}
            >
              ×
            </button>
          </div>
          <div className={styles['lesson-modal-body']}>
            {availableLessons.length === 0 ? (
              <p className={styles['lesson-modal-empty']}>No lessons available. Please check your subscriptions.</p>
            ) : (
              <div className={styles['lesson-grid']}>
                {availableLessons.map((lesson) => (
                  <div key={lesson._id} className={styles['lesson-card']}>
                    <div className={styles['lesson-card-content']}>
                      <h6 className={styles['lesson-card-title']}>{lesson.name}</h6>
                      <p className={styles['lesson-card-description']}>
                        {lesson.description ? lesson.description.substring(0, 150) + '...' : 'No description available'}
                      </p>
                      <button
                        className={styles['lesson-select-btn']}
                        onClick={() => handleSelectLesson(lesson)}
                      >
                        Select This Lesson
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={styles['lesson-modal-footer']}>
            <button
              type="button"
              className={styles['lesson-close-btn']}
              onClick={handleCloseLessonSelector}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Student Work Modal */}
    {showStudentWorkModal && studentWorkData && (
      <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Student Work: {studentWorkData.student.firstName} {studentWorkData.student.lastName}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowStudentWorkModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong style={{ color: 'var(--darkOrange)' }}>{studentWorkData.mission ? 'Mission' : 'Quiz'}:</strong> 
                  <span style={{ color: '#FFB366', marginLeft: '8px' }}>
                    {studentWorkData.mission?.title || studentWorkData.quiz?.title}
                  </span>
                </div>
                <div className="col-md-6">
                  <strong style={{ color: 'var(--darkOrange)' }}>Student Code:</strong> 
                  <span style={{ color: '#FFB366', marginLeft: '8px' }}>{studentWorkData.student.studentCode}</span>
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong style={{ color: 'var(--darkOrange)' }}>Status:</strong> 
                  <span className={`badge ms-2 ${
                    studentWorkData.assignment.status === 'completed' ? 'bg-success' : 'bg-secondary'
                  }`}>
                    {studentWorkData.assignment.status.charAt(0).toUpperCase() + 
                     studentWorkData.assignment.status.slice(1).replace('_', ' ')}
                  </span>
                </div>
                <div className="col-md-6">
                  <strong style={{ color: 'var(--darkOrange)' }}>Completed:</strong> 
                  <span style={{ color: '#FFB366', marginLeft: '8px' }}>
                    {studentWorkData.assignment.completedAt 
                      ? new Date(studentWorkData.assignment.completedAt).toLocaleString()
                      : 'Not completed'}
                  </span>
                </div>
              </div>

              {/* Quiz Results Display */}
              {studentWorkData.quiz && studentWorkData.assignment.status === 'completed' && (
                <div className="mt-4">
                  <h6 style={{ color: 'var(--darkOrange)' }}>Quiz Results:</h6>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong style={{ color: 'var(--darkOrange)' }}>Score:</strong> 
                      <span style={{ color: '#FFB366', marginLeft: '8px' }}>
                        {studentWorkData.assignment.score || studentWorkData.assignment.percentage || 0}%
                      </span>
                    </div>
                    <div className="col-md-6">
                      <strong style={{ color: 'var(--darkOrange)' }}>Attempts:</strong> 
                      <span style={{ color: '#FFB366', marginLeft: '8px' }}>
                        {studentWorkData.assignment.attempts || 1}
                      </span>
                    </div>
                  </div>

                  {studentWorkData.quiz.questions && studentWorkData.quiz.questions.length > 0 && (
                    <div className="quiz-questions">
                      <h6 style={{ color: 'var(--darkOrange)', marginBottom: '1rem' }}>Questions and Answers:</h6>
                      {studentWorkData.quiz.questions.map((question, index) => {
                        const studentAnswer = studentWorkData.studentResponses?.[question._id];
                        const correctAnswer = question.correctAnswer;
                        const _isCorrect = studentAnswer === correctAnswer;

                        return (
                          <div key={question._id} className="card mb-3" style={{ border: '1px solid var(--darkPurple)' }}>
                            <div className="card-header" style={{ backgroundColor: 'var(--lightPurple)' }}>
                              <h6 className="mb-0" style={{ color: 'var(--darkOrange)' }}>
                                Question {index + 1}: {question.question}
                              </h6>
                            </div>
                            <div className="card-body">
                              {question.type === 'multiple-choice' && question.options && (
                                <div className="options-list">
                                  {question.options.map((option, optionIndex) => {
                                    const isStudentAnswer = studentAnswer === optionIndex;
                                    const isCorrectOption = optionIndex === correctAnswer;
                                    
                                    return (
                                      <div 
                                        key={optionIndex} 
                                        className={`option-item p-2 mb-2 rounded ${
                                          isCorrectOption ? 'bg-success text-white' : 
                                          isStudentAnswer && !isCorrectOption ? 'bg-danger text-white' : 
                                          'bg-light'
                                        }`}
                                        style={{ 
                                          border: isCorrectOption ? '2px solid #28a745' : 
                                                 isStudentAnswer && !isCorrectOption ? '2px solid #dc3545' : 
                                                 '1px solid #dee2e6'
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <span className="me-2 fw-bold">{String.fromCharCode(65 + optionIndex)}.</span>
                                          <span>{option}</span>
                                          {isCorrectOption && (
                                            <span className="ms-auto badge bg-light text-success">
                                              ✓ Correct Answer
                                            </span>
                                          )}
                                          {isStudentAnswer && !isCorrectOption && (
                                            <span className="ms-auto badge bg-light text-danger">
                                              ✗ Student's Answer
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {question.type === 'true-false' && question.options && (
                                <div className="options-list">
                                  {question.options.map((option, optionIndex) => {
                                    const isStudentAnswer = studentAnswer === optionIndex;
                                    const isCorrectOption = optionIndex === correctAnswer;
                                    
                                    return (
                                      <div 
                                        key={optionIndex} 
                                        className={`option-item p-2 mb-2 rounded ${
                                          isCorrectOption ? 'bg-success text-white' : 
                                          isStudentAnswer && !isCorrectOption ? 'bg-danger text-white' : 
                                          'bg-light'
                                        }`}
                                        style={{ 
                                          border: isCorrectOption ? '2px solid #28a745' : 
                                                 isStudentAnswer && !isCorrectOption ? '2px solid #dc3545' : 
                                                 '1px solid #dee2e6'
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <span className="me-2 fw-bold">{optionIndex === 0 ? 'True' : 'False'}</span>
                                          <span>{option}</span>
                                          {isCorrectOption && (
                                            <span className="ms-auto badge bg-light text-success">
                                              ✓ Correct Answer
                                            </span>
                                          )}
                                          {isStudentAnswer && !isCorrectOption && (
                                            <span className="ms-auto badge bg-light text-danger">
                                              ✗ Student's Answer
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {(question.type === 'fill-in-the-blank' || question.questionType === 'fill-in-the-blank') && (
                                <div className="fill-in-answer">
                                  <div className="mb-2">
                                    <strong style={{ color: 'var(--darkOrange)' }}>Student's Answer:</strong>
                                    <div className="p-2 bg-light rounded" style={{ color: '#FFB366' }}>
                                      {studentAnswer || 'No answer provided'}
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <strong style={{ color: 'var(--darkOrange)' }}>Correct Answer:</strong>
                                    <div className="p-2 bg-success text-white rounded">
                                      {question.correctAnswer}
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {question.type === 'essay' && (
                                <div className="essay-answer">
                                  <div className="mb-2">
                                    <strong style={{ color: 'var(--darkOrange)' }}>Student's Answer:</strong>
                                    <div className="p-3 bg-light rounded" style={{ color: '#FFB366', minHeight: '100px' }}>
                                      {studentAnswer || 'No answer provided'}
                                    </div>
                                  </div>
                                  
                                  {/* Show existing grade if available */}
                                  {studentWorkData.assignment.essayGrades && studentWorkData.assignment.essayGrades[question._id] && (
                                    <div className="mb-2">
                                      <strong style={{ color: 'var(--darkOrange)' }}>Grade:</strong>
                                      <span className="badge bg-success ms-2">
                                        {studentWorkData.assignment.essayGrades[question._id].grade}%
                                      </span>
                                      {studentWorkData.assignment.essayGrades[question._id].feedback && (
                                        <div className="mt-2">
                                          <strong style={{ color: 'var(--darkOrange)' }}>Feedback:</strong>
                                          <div className="p-2 bg-light rounded mt-1" style={{ color: '#FFB366' }}>
                                            {studentWorkData.assignment.essayGrades[question._id].feedback}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Grade button */}
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                      <em>Essay questions require manual grading</em>
                                    </div>
                                    <button 
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleGradeEssay(question, studentAnswer)}
                                      disabled={!studentAnswer}
                                    >
                                      {studentWorkData.assignment.essayGrades && studentWorkData.assignment.essayGrades[question._id] 
                                        ? 'Update Grade' 
                                        : 'Grade Essay'
                                      }
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {studentWorkData.assignment.progress && (
                <div className="mt-4">
                  <h6 style={{ color: 'var(--darkOrange)' }}>Student's Work:</h6>
                  
                  {studentWorkData.assignment.progress.completedTasks && 
                   studentWorkData.assignment.progress.completedTasks.length > 0 && (
                    <div className="mb-3">
                      <strong style={{ color: 'var(--darkOrange)' }}>Completed Tasks:</strong>
                      <ul className="mt-2">
                        {studentWorkData.assignment.progress.completedTasks.map((taskIndex, idx) => (
                          <li key={idx} style={{ color: '#FFB366' }}>Task {taskIndex + 1}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {studentWorkData.assignment.progress.notes && (
                    <div className="mb-3">
                      <strong style={{ color: 'var(--darkOrange)' }}>Student Notes:</strong>
                      <div className="mt-2 p-3 bg-light rounded" style={{ backgroundColor: '#FFF8DC' }}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, color: 'var(--darkOrange)' }}>
                          {studentWorkData.assignment.progress.notes}
                        </pre>
                      </div>
                    </div>
                  )}

                  {studentWorkData.assignment.progress.lastSaved && (
                    <div className="text-muted small">
                      <strong style={{ color: 'var(--darkOrange)' }}>Last saved:</strong> 
                      <span style={{ color: '#FFB366', marginLeft: '8px' }}>
                        {new Date(studentWorkData.assignment.progress.lastSaved).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {(!studentWorkData.assignment.progress || 
                (!studentWorkData.assignment.progress.completedTasks?.length && 
                 !studentWorkData.assignment.progress.notes)) && 
               !(studentWorkData.quiz && (studentWorkData.assignment.status === 'completed' || studentWorkData.assignment.status === 'graded')) && (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  No work submitted by the student yet.
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ backgroundColor: 'var(--lightPurple)', borderTop: '1px solid var(--darkPurple)' }}>
              <button 
                type="button" 
                className="btn btn-warning me-2" 
                onClick={() => setShowReassignModal(true)}
                title="Re-assign this mission if the student's work needs improvement"
              >
                <i className="fas fa-redo me-1"></i>
                Re-assign Mission
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowStudentWorkModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Re-assign Mission Modal */}
    {showReassignModal && studentWorkData && (
      <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-redo text-warning me-2"></i>
                Re-assign Mission
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowReassignModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Re-assign {studentWorkData.mission ? 'Mission' : 'Quiz'}:</strong> This will change the status back to "assigned" but 
                <strong> preserve the student's work</strong>. The student can continue from where they left off.
              </div>
              
              <div className="mb-3">
                <strong>Student:</strong> {studentWorkData.student.firstName} {studentWorkData.student.lastName}
              </div>
              
              <div className="mb-3">
                <strong>{studentWorkData.mission ? 'Mission' : 'Quiz'}:</strong> {studentWorkData.mission?.title || studentWorkData.quiz?.title}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  <strong>Reason for Re-assignment:</strong>
                  <small className="text-muted d-block">Explain why the student needs to redo this {studentWorkData.mission ? 'mission' : 'quiz'}</small>
                </label>
                <textarea
                  className="form-control"
                  placeholder="e.g., Work needs improvement, incomplete response, incorrect approach..."
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowReassignModal(false)}
                disabled={reassigningMission}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-warning" 
                onClick={handleReassignMission}
                disabled={reassigningMission || !reassignReason.trim()}
              >
                {reassigningMission ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Re-assigning...
                  </>
                ) : (
                  <>
                    <i className="fas fa-redo me-1"></i>
                    Re-assign Mission
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Essay Grading Modal */}
    {showEssayGrading && currentEssayQuestion && (
      <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-edit text-primary me-2"></i>
                Grade Essay Question {currentEssayQuestion.questionIndex + 1}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowEssayGrading(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <strong style={{ color: 'var(--darkOrange)' }}>Question:</strong>
                <div className="p-3 bg-light rounded mt-2" style={{ color: '#FFB366' }}>
                  {currentEssayQuestion.question.question}
                </div>
              </div>
              
              <div className="mb-3">
                <strong style={{ color: 'var(--darkOrange)' }}>Student's Answer:</strong>
                <div className="p-3 bg-light rounded mt-2" style={{ color: '#FFB366', minHeight: '150px' }}>
                  {currentEssayQuestion.studentAnswer || 'No answer provided'}
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">
                    <strong style={{ color: 'var(--darkOrange)' }}>Grade (0-100):</strong>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="100"
                    value={essayGrade}
                    onChange={(e) => setEssayGrade(e.target.value)}
                    placeholder="Enter grade (0-100)"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    <strong style={{ color: 'var(--darkOrange)' }}>Points Available:</strong>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentEssayQuestion.question.points || 'Not specified'}
                    disabled
                  />
                </div>
              </div>
              
              <div className="mt-3">
                <label className="form-label">
                  <strong style={{ color: 'var(--darkOrange)' }}>Feedback (Optional):</strong>
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={essayFeedback}
                  onChange={(e) => setEssayFeedback(e.target.value)}
                  placeholder="Provide feedback to the student about their essay response..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowEssayGrading(false)}
                disabled={gradingEssay}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSubmitEssayGrade}
                disabled={gradingEssay || !essayGrade || isNaN(essayGrade) || essayGrade < 0 || essayGrade > 100}
              >
                {gradingEssay ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Grading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-1"></i>
                    Save Grade
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Notification Modal */}
    <NotificationModal
      show={notificationModal.show}
      onClose={hideNotification}
      title={notificationModal.title}
      message={notificationModal.message}
      type={notificationModal.type}
    />

    {/* Quiz Creation Modal */}
    <QuizCreationModal
      show={showQuizCreationModal}
      onHide={() => setShowQuizCreationModal(false)}
      onQuizCreated={(_quiz) => {
        setShowQuizCreationModal(false);
        // Switch to Quizzes tab after successful save (same as missions)
        setActiveTab('quizzes');
        showToast('Quiz created successfully!', 'success');
      }}
      curriculumData={curriculumData}
      subscriptionLessons={getSelectableSubscriptionLessons(assets)}
    />
    </>
  );
};

export default TeacherDashboard;
